/**
 * Assets - Fleet vehicle list and home depot configuration.
 * Asset Details shows the Truth Layer vehicle roster
 * (GET fleet-intelligence/vehicles/list) — canonical, identity-resolved
 * across every ingest source (Samsara, Supatrak, Optifleet, Aquarius, ...),
 * already deduplicated when a vehicle exists in more than one source. Manual
 * edits (type, home depot, unit cost, etc.) and manually-added vehicles are
 * stored as VehicleAttributesDeclared canonical events
 * (PUT fleet-intelligence/vehicles/attributes) rather than a config blob —
 * each edit is a new event superseding the prior one, never an in-place
 * mutation. Home Depot manages depot locations.
 */

import { countTrailerAssets, trailerDeckFromVehicleType } from '../utils/fleetCostTrailerStanding';
import { clearFleetCostAssetClassLabelsCache } from '../utils/fleetCostAssetLabels';
import {
  PAYLOAD_OPTIONS_LIST_ID,
  parseTypicalTonnes,
  payloadCostedAsDefault,
  payloadTonnesSuggestions,
} from '../utils/assetPayloadTonnes';
import {
  clearAssetsRosterSessionCopy,
  readAssetsRosterSessionCopy,
  writeAssetsRosterSessionCopy,
} from '../utils/assetsRosterSessionCopy';
import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Truck, Download, RefreshCw, Save, ChevronUp, ChevronDown, Plus, Trash2, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { setUnsavedChangesGuard } from '../utils/unsavedChangesGuard';
import ConfigMetaBadge from '../components/config/ConfigMetaBadge';
import { ConfigChangeHistoryButton } from '../components/config/ConfigChangeHistoryPanel';
import { getCurrentUserEmail, readSessionUserEmail } from '../auth/cognitoAuth';
import { fetchConfigAssumptionsHistory } from '../services/configAssumptionsHistoryClient';
import {
  assetConfirmKey,
  assetFieldInputClass,
  assetFieldStatusTitle,
  assetFieldTextClass,
  collectAssertedAssetFieldOverrides,
  CONFIRMABLE_VEHICLE_FIELDS,
  hasUnconfirmedAssertedOverrides,
  resolveAssetFieldStatus,
  type AssetFieldConfirmations,
  type ConfirmableVehicleField,
} from '../utils/assetFieldConfirmations';
import {
  decideEnrichedRosterApply,
  progressiveRosterBanner,
} from '../utils/vehicleListProgressive';
import { samsaraDeviceNotesByReg, type SamsaraDeviceNote } from '../utils/samsaraDeviceNotes';
import {
  displayPlateFromRegistration,
  fleetIdsForTipAquariusJoin,
  isDigitsOnlyRegistration,
} from '../utils/vehicleDisplayPlate';
import { UNKNOWN_CELL } from '../utils/uiText';
import AssetsDrivers from '../components/assets/AssetsDrivers';
import {
  CAPABILITY_FIELD_LABELS,
  CAPABILITY_FIELD_TITLES,
  YES_NO_CAPABILITY_FIELDS,
  YES_NO_OPTIONS,
  fillSuggestedCapabilities,
  formatPalletSpaces,
  formatYesNo,
  hasFillableSuggestions,
  suggestionFor,
  suggestionTitle,
  yesNoLabel,
  type InferredCapabilities,
  type YesNoCapabilityField,
} from '../utils/assetCapabilities';

// Keep in sync with REGISTRATION_TYPE_ALIASES in
// zeecore/src/truth/identity/normalise.ts (isRegistrationType) — a vehicle
// whose sole identifier uses 'vrn' or 'vehicle_registration' must resolve
// the same way here as it does for ZOE's list_fleet_assets roster.
const REGISTRATION_TYPE_ALIASES = ['registration', 'vrn', 'vehicle_registration'];
function isRegistrationType(idType: string): boolean {
  return REGISTRATION_TYPE_ALIASES.includes(idType.toLowerCase());
}

type EditableVehicleField =
  | 'vehicle_reg'
  | 'type'
  | 'start_year'
  | 'service_life_yr'
  | 'circuit_name'
  | 'fuel'
  | 'typical_tonnes'
  | 'unit_cost'
  | 'insurance_yr'
  | 'acquisition'
  | 'trailer_deck'
  | 'tail_lift'
  | 'adr_equipped'
  | 'moffett'
  | 'small_vehicle'
  | 'pallet_spaces';

type BulkEditableVehicleField = Exclude<EditableVehicleField, 'vehicle_reg'>;

/**
 * Every editable attribute can be confirmed: a field missing from
 * CONFIRMABLE_VEHICLE_FIELDS turns blue (Asserted) but Confirm never stamps it.
 * Fails to compile when a new column is not added there.
 */
type UnconfirmableEditableField = Exclude<BulkEditableVehicleField, ConfirmableVehicleField>;
const EVERY_EDITABLE_FIELD_CONFIRMABLE: [UnconfirmableEditableField] extends [never] ? true : UnconfirmableEditableField = true;
void EVERY_EDITABLE_FIELD_CONFIRMABLE;

const BULK_EDIT_FIELD_LABELS: Record<BulkEditableVehicleField, string> = {
  type: 'Vehicle Type',
  start_year: 'Start Year',
  service_life_yr: 'Service Life (yr)',
  circuit_name: 'Home Depot',
  typical_tonnes: 'Payload (T)',
  fuel: 'Fuel Type',
  unit_cost: 'Unit Cost (£)',
  insurance_yr: 'Insurance (£/yr)',
  acquisition: 'Acquisition',
  trailer_deck: 'Deck (trailers)',
  tail_lift: 'Tail-lift',
  adr_equipped: 'ADR equipped',
  moffett: 'Moffett',
  small_vehicle: 'Small vehicle',
  pallet_spaces: 'Pallet spaces',
};

const isYesNoCapabilityField = (f: string): f is YesNoCapabilityField =>
  (YES_NO_CAPABILITY_FIELDS as readonly string[]).includes(f);

/** Stored as purchase / lease / rental (VehicleAttributesDeclared.acquisition). */
const ACQUISITION_OPTIONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'lease', label: 'Lease' },
  { value: 'rental', label: 'Rental' },
] as const;

/** Canonical acquisition value, or '' when unset / unrecognised (saved as no value). */
function formatAcquisition(value: unknown): string {
  const s = String(value ?? '').trim().toLowerCase();
  return ACQUISITION_OPTIONS.some((o) => o.value === s) ? s : '';
}

function acquisitionLabel(value: unknown): string {
  const v = formatAcquisition(value);
  return ACQUISITION_OPTIONS.find((o) => o.value === v)?.label ?? UNKNOWN_CELL;
}

/** Stored as single / double (VehicleAttributesDeclared.trailer_deck); trailers only. */
const TRAILER_DECK_OPTIONS = [
  { value: 'single', label: 'Single deck' },
  { value: 'double', label: 'Double deck' },
] as const;

function formatTrailerDeck(value: unknown): string {
  const s = String(value ?? '').trim().toLowerCase();
  return TRAILER_DECK_OPTIONS.some((o) => o.value === s) ? s : '';
}

function trailerDeckLabel(value: unknown): string {
  const v = formatTrailerDeck(value);
  return TRAILER_DECK_OPTIONS.find((o) => o.value === v)?.label ?? UNKNOWN_CELL;
}

/** Deck applies to trailers only: the same rule the cost tables count trailers by. */
function isTrailerRow(row: VehicleDetailsRow): boolean {
  return countTrailerAssets([{ type: String(row.type ?? ''), payloadTonnes: String(row.typical_tonnes ?? '') }]) === 1;
}

/** Named ingest / declare sources shown in Assets filters and Data Source badges. */
type NamedVehicleSource = 'supatrak' | 'optifleet' | 'samsara' | 'aquarius' | 'tip' | 'manual';

/** 'multiple' is a synthetic filter for vehicles claimed by 2+ systems (no single source). */
type DataSourceFilter = 'all' | NamedVehicleSource | 'multiple';

const NAMED_SOURCE_ORDER: NamedVehicleSource[] = [
  'samsara',
  'aquarius',
  'tip',
  'supatrak',
  'optifleet',
  'manual',
];

/** Data sources filter chips show the same sources as the badge, in the same order. */
const FILTERABLE_SOURCE_ORDER: NamedVehicleSource[] = NAMED_SOURCE_ORDER;

/**
 * Categorical data-source identity colors — one stable chart-palette slot per
 * source, assigned in NAMED_SOURCE_ORDER (samsara → chart-1, …, manual →
 * chart-6). 'multiple' takes chart-7. Categorical only: no status meaning.
 */
const SOURCE_META: Record<
  NamedVehicleSource,
  { label: string; badgeClass: string; chipIdle: string; chipActive: string }
> = {
  samsara: {
    label: 'Samsara',
    badgeClass: 'bg-chart-1/25 text-foreground border-chart-1/60',
    chipIdle: 'bg-chart-1 border-chart-1 hover:bg-chart-1 text-white',
    chipActive: 'ring-2 ring-offset-1 ring-offset-card ring-chart-1 bg-chart-1 border-chart-1 text-white',
  },
  aquarius: {
    label: 'Aquarius',
    badgeClass: 'bg-chart-2/25 text-foreground border-chart-2/60',
    chipIdle: 'bg-chart-2 border-chart-2 hover:bg-chart-2 text-white',
    chipActive: 'ring-2 ring-offset-1 ring-offset-card ring-chart-2 bg-chart-2 border-chart-2 text-white',
  },
  tip: {
    label: 'TIP',
    badgeClass: 'bg-chart-3/25 text-foreground border-chart-3/70',
    chipIdle: 'bg-chart-3 border-chart-3 hover:bg-chart-3 text-white',
    chipActive: 'ring-2 ring-offset-1 ring-offset-card ring-chart-3 bg-chart-3 border-chart-3 text-white',
  },
  supatrak: {
    label: 'Supatrak',
    badgeClass: 'bg-chart-4/25 text-foreground border-chart-4/60',
    chipIdle: 'bg-chart-4 border-chart-4 hover:bg-chart-4 text-white',
    chipActive: 'ring-2 ring-offset-1 ring-offset-card ring-chart-4 bg-chart-4 border-chart-4 text-white',
  },
  optifleet: {
    label: 'Optifleet',
    badgeClass: 'bg-chart-5/25 text-foreground border-chart-5/60',
    chipIdle: 'bg-chart-5 border-chart-5 hover:bg-chart-5 text-white',
    chipActive: 'ring-2 ring-offset-1 ring-offset-card ring-chart-5 bg-chart-5 border-chart-5 text-white',
  },
  manual: {
    label: 'Manual',
    badgeClass: 'bg-chart-6/25 text-foreground border-chart-6/60',
    chipIdle: 'text-white bg-chart-6 border-chart-6 hover:bg-chart-6',
    chipActive: 'ring-2 ring-offset-1 ring-offset-card ring-chart-6 text-white bg-chart-6 border-chart-6',
  },
};

/** Distinct pill style when a vehicle is claimed by 2+ systems (chart-7 — a slot no named source uses). */
const MULTI_SOURCE_BADGE_CLASS = 'bg-chart-7/25 text-foreground border-chart-7/60';
const MULTI_SOURCE_CHIP_IDLE = 'bg-chart-7 border-chart-7 hover:bg-chart-7 text-white';
const MULTI_SOURCE_CHIP_ACTIVE =
  'ring-2 ring-offset-1 ring-offset-card ring-chart-7 bg-chart-7 border-chart-7 text-white';

function orderedNamedSources(systems: Set<string> | undefined): NamedVehicleSource[] {
  if (!systems || systems.size === 0) return [];
  return NAMED_SOURCE_ORDER.filter((s) => systems.has(s));
}

/**
 * Badge sources: vendor systems only. "Manual" is reserved for user-created
 * table rows (or vehicles with no vendor claim at all).
 */
function sourcesForDataSourceBadge(systems: Set<string> | undefined): NamedVehicleSource[] {
  const named = orderedNamedSources(systems);
  const vendors = named.filter((s) => s !== 'manual');
  if (vendors.length > 0) return vendors;
  if (named.includes('manual')) return ['manual'];
  return [];
}

function dataSourceBadgeLabel(named: NamedVehicleSource[], isNewRow: boolean): string {
  if (isNewRow) return 'Manual';
  if (named.length === 0) return '—';
  return named.map((s) => SOURCE_META[s].label).join(' + ');
}

function dataSourceBadgeClass(named: NamedVehicleSource[], isNewRow: boolean): string {
  if (isNewRow) return SOURCE_META.manual.badgeClass;
  if (named.length === 0) return SOURCE_META.manual.badgeClass;
  if (named.length >= 2) return MULTI_SOURCE_BADGE_CLASS;
  return SOURCE_META[named[0]].badgeClass;
}


type ConfirmAction =
  | 'delete-vehicle'
  | 'delete-depot'
  | 'refresh-vehicles'
  | 'refresh-depots'
  | null;

export type VehicleDetailsRow = Record<string, string | number>;

/** Row fields plus synthetic Data Source column (derived from claim systems). */
type SortableVehicleColumn = keyof VehicleDetailsRow | 'data_source';

type HomeDepotRow = {
  id: string;
  name: string;
  depotNumber: string;
  postcode: string;
  latitude: number;
  longitude: number;
  type: string;
  showOnMap: boolean;
  /** Palletways member we operate — COL/DEL and fleet tenancy. Missing defaults false. */
  owned: boolean;
};

type EditableDepotField = 'name' | 'depotNumber' | 'postcode' | 'type';

const DEPOT_TYPE_OPTIONS = ['Depot', 'Consolidation Centre'] as const;


/** Store/display payload as a plain number; column header already indicates tonnes (T). */
function formatTypicalTonnes(num: string): string {
  const parsed = parseTypicalTonnes(num);
  if (!parsed) return '—';
  return parsed;
}

function parseServiceLifeYr(s: string): string {
  const raw = String(s ?? '').trim().replace(/t+$/i, '');
  if (!raw || raw === '—') return '';
  const match = raw.match(/\d+(?:\.\d*)?/);
  return match ? match[0] : raw;
}

function formatServiceLifeYr(num: string): string {
  const parsed = parseServiceLifeYr(num);
  if (!parsed) return '—';
  return parsed;
}

/** Samsara fleet-vehicles `year` (model / start year) for display. */
function formatStartYear(value: unknown): string {
  if (value == null || value === '') return '—';
  const asNum = Number(value);
  if (Number.isFinite(asNum) && asNum >= 1900 && asNum <= 2100) return String(Math.trunc(asNum));
  const s = String(value).trim();
  return s || '—';
}

function parseMoneyGbp(value: unknown): string {
  const raw = String(value ?? '').trim().replace(/£/g, '').replace(/,/g, '');
  if (!raw || raw === '—') return '';
  const match = raw.match(/-?\d+(?:\.\d*)?/);
  return match ? match[0] : '';
}

/** Store/display money as a plain number; column header already shows £. */
function formatMoneyGbp(value: unknown): string {
  const parsed = parseMoneyGbp(value);
  if (!parsed) return '—';
  const n = Number(parsed);
  if (!Number.isFinite(n)) return '—';
  return String(n);
}

function normalizeVehicleRow(row: VehicleDetailsRow): VehicleDetailsRow {
  return {
    ...row,
    service_life_yr: formatServiceLifeYr(String(row.service_life_yr ?? '')),
    typical_tonnes: formatTypicalTonnes(String(row.typical_tonnes ?? '')),
    start_year: formatStartYear(row.start_year),
    unit_cost: formatMoneyGbp(row.unit_cost),
    insurance_yr: formatMoneyGbp(row.insurance_yr),
    acquisition: formatAcquisition(row.acquisition),
    trailer_deck: formatTrailerDeck(row.trailer_deck),
    tail_lift: formatYesNo(row.tail_lift),
    adr_equipped: formatYesNo(row.adr_equipped),
    moffett: formatYesNo(row.moffett),
    small_vehicle: formatYesNo(row.small_vehicle),
    pallet_spaces: formatPalletSpaces(row.pallet_spaces),
  };
}

function observedVehicleRowFromApi(params: {
  reg: string;
  vehicleType?: string;
  startYear?: number;
}): VehicleDetailsRow {
  return normalizeVehicleRow({
    vehicle_reg: params.reg,
    type: params.vehicleType ?? '',
    service_life_yr: '—',
    start_year: params.startYear ? String(params.startYear) : '—',
    circuit_name: '',
    fuel: '',
    metric: '—',
    metric_range_tonnes: '—',
    typical_tonnes: '—',
    unit_cost: '—',
    insurance_yr: '—',
    acquisition: '',
    // "DD Trailer" from the source says double deck: Observed, not an edit.
    trailer_deck: trailerDeckFromVehicleType(params.vehicleType) ?? '',
    // No source system sends equipment: never Observed.
    tail_lift: '',
    adr_equipped: '',
    moffett: '',
    small_vehicle: '',
    pallet_spaces: '',
  });
}

type VehiclesListApiItem = {
  registration?: string;
  vehicleType?: string;
  startYear?: number;
  /** VehicleObserved-only values (no declared overlay) — Observed vs Asserted baseline. */
  observedVehicleType?: string;
  observedStartYear?: number;
  identifiers?: Array<{ type: string; value: string; sourceSystem: string }>;
  source?: string;
  declaredAttributes?: Record<string, string>;
  /** Suggested equipment (provenance Inferred) — shown only in empty cells. */
  inferredCapabilities?: InferredCapabilities;
  /** The vehicle's GPS device moved to, or came from, another vehicle on this list. */
  samsaraDevice?: SamsaraDeviceNote;
};


/** Map /vehicles/list payload → table rows + source badges (tip/aquarius safety net). */
function mapVehiclesListToDetails(vehicles: VehiclesListApiItem[]): {
  rows: VehicleDetailsRow[];
  sources: Map<string, Set<string>>;
  observedByReg: Map<string, VehicleDetailsRow>;
  inferredByReg: Map<string, InferredCapabilities>;
} {
  const sources = new Map<string, Set<string>>();
  const rowsByReg = new Map<string, VehicleDetailsRow>();
  const observedByReg = new Map<string, VehicleDetailsRow>();
  const inferredByReg = new Map<string, InferredCapabilities>();
  const plateByFleetId = new Map<string, string>();
  type DeferredDigits = {
    fleetKey: string;
    systems: Set<string>;
    incoming: VehicleDetailsRow;
    observed: VehicleDetailsRow;
    inferred?: InferredCapabilities;
  };
  const deferredDigits: DeferredDigits[] = [];

  const mergeObservedIntoPlate = (reg: string, incoming: VehicleDetailsRow) => {
    if (!reg) return;
    const existing = observedByReg.get(reg);
    if (!existing) {
      observedByReg.set(reg, { ...incoming });
      return;
    }
    for (const key of Object.keys(incoming) as Array<keyof VehicleDetailsRow>) {
      if (key === 'vehicle_reg') continue;
      const cur = String(existing[key] ?? '').trim();
      const next = String(incoming[key] ?? '').trim();
      if ((!cur || cur === '—') && next && next !== '—') {
        (existing as Record<string, unknown>)[key] = incoming[key];
      }
    }
  };

  const mergeIntoPlateRow = (
    reg: string,
    incoming: VehicleDetailsRow,
    systems: Set<string>,
    observed?: VehicleDetailsRow,
  ) => {
    if (observed) mergeObservedIntoPlate(reg, observed);
    const existingRow = rowsByReg.get(reg);
    if (!existingRow) {
      rowsByReg.set(reg, incoming);
      sources.set(reg, systems);
      return;
    }
    const existingSystems = sources.get(reg) ?? new Set<string>();
    const tipAquarius =
      (existingSystems.has('tip') || systems.has('tip'))
      && (existingSystems.has('aquarius') || systems.has('aquarius'));
    if (tipAquarius) {
      for (const key of Object.keys(incoming) as Array<keyof VehicleDetailsRow>) {
        if (key === 'vehicle_reg') continue;
        const cur = String(existingRow[key] ?? '').trim();
        const next = String(incoming[key] ?? '').trim();
        if ((!cur || cur === '—') && next && next !== '—') {
          (existingRow as Record<string, unknown>)[key] = incoming[key];
        }
      }
    } else {
      console.warn(
        `[Assets] Plate ${reg} claimed by two entities with no tip+aquarius pair — `
          + 'keeping one row, merging source badges (server should dedupe).',
      );
    }
    for (const s of systems) existingSystems.add(s);
    sources.set(reg, existingSystems);
  };

  for (const v of vehicles) {
    const tipPlate = v.identifiers?.find(
      (id) =>
        id.sourceSystem === 'tip'
        && isRegistrationType(id.type)
        && String(id.value ?? '').trim(),
    )?.value;
    const hasTip =
      v.source === 'tip'
      || v.identifiers?.some((id) => id.sourceSystem === 'tip') === true;
    const rawReg = String(
      (hasTip && tipPlate)
        ? tipPlate
        : (v.registration
          ?? v.identifiers?.find((id) => isRegistrationType(id.type))?.value
          ?? '')
    ).trim();
    const reg = displayPlateFromRegistration(rawReg);
    if (!reg) continue;

    const systems = new Set((v.identifiers ?? []).map((id) => id.sourceSystem).filter(Boolean));
    if (systems.size === 0 && v.source && v.source !== 'unknown' && v.source !== 'declared') {
      systems.add(v.source);
    }

    const declared = v.declaredAttributes ?? {};
    // Observed-only values: vehicleType/startYear already prefer declared
    // attributes, so they would make every Assets edit look Observed.
    const observed = observedVehicleRowFromApi({
      reg,
      vehicleType: v.observedVehicleType,
      startYear: v.observedStartYear,
    });
    const incoming = normalizeVehicleRow({
      vehicle_reg: reg,
      type: declared.type ?? v.vehicleType ?? '',
      service_life_yr: declared.service_life_yr ?? '—',
      start_year: declared.start_year ?? (v.startYear ? String(v.startYear) : '—'),
      circuit_name: declared.circuit_name ?? '',
      fuel: declared.fuel ?? '',
      metric: declared.metric ?? '—',
      metric_range_tonnes: declared.metric_range_tonnes ?? '—',
      typical_tonnes: declared.typical_tonnes ?? '—',
      unit_cost: declared.unit_cost ?? '—',
      insurance_yr: declared.insurance_yr ?? '—',
      acquisition: declared.acquisition ?? '',
      trailer_deck: declared.trailer_deck ?? trailerDeckFromVehicleType(v.observedVehicleType) ?? '',
      tail_lift: declared.tail_lift ?? '',
      adr_equipped: declared.adr_equipped ?? '',
      moffett: declared.moffett ?? '',
      small_vehicle: declared.small_vehicle ?? '',
      pallet_spaces: declared.pallet_spaces ?? '',
    });

    const aquariusOnly =
      systems.size > 0
      && [...systems].every((s) => s === 'aquarius');
    if (aquariusOnly && isDigitsOnlyRegistration(reg)) {
      deferredDigits.push({
        fleetKey: reg.replace(/\s+/g, '').toUpperCase(),
        systems,
        incoming,
        observed,
        inferred: v.inferredCapabilities,
      });
      continue;
    }

    mergeIntoPlateRow(reg, incoming, systems, observed);
    if (v.inferredCapabilities && !inferredByReg.has(reg)) inferredByReg.set(reg, v.inferredCapabilities);
    for (const fleetId of fleetIdsForTipAquariusJoin(v.identifiers)) {
      if (!plateByFleetId.has(fleetId)) plateByFleetId.set(fleetId, reg);
    }
  }

  for (const deferred of deferredDigits) {
    const tipPlate = plateByFleetId.get(deferred.fleetKey);
    const finalReg = tipPlate ?? String(deferred.incoming.vehicle_reg ?? '');
    if (deferred.inferred && finalReg && !inferredByReg.has(finalReg)) inferredByReg.set(finalReg, deferred.inferred);
    if (tipPlate) {
      mergeIntoPlateRow(
        tipPlate,
        { ...deferred.incoming, vehicle_reg: tipPlate },
        deferred.systems,
        { ...deferred.observed, vehicle_reg: tipPlate },
      );
    } else {
      mergeIntoPlateRow(
        String(deferred.incoming.vehicle_reg ?? ''),
        deferred.incoming,
        deferred.systems,
        deferred.observed,
      );
    }
  }

  return {
    rows: [...rowsByReg.values()],
    sources,
    observedByReg,
    inferredByReg,
  };
}

export default function Assets() {
  const [vehicleDetails, setVehicleDetails] = useState<VehicleDetailsRow[]>([]);
  const [vehicleListLoading, setVehicleListLoading] = useState(false);
  const [vehicleListEnriching, setVehicleListEnriching] = useState(false);
  /** True once the roster on screen came from a full (VehicleObserved-enriched) list. */
  const [observedBaselineReady, setObservedBaselineReady] = useState(false);
  /** Full roster arrived while the user had unsaved edits — apply discards those edits. */
  const [pendingEnrichedRoster, setPendingEnrichedRoster] = useState<{
    rows: VehicleDetailsRow[];
    sources: Map<string, Set<string>>;
    observedByReg: Map<string, VehicleDetailsRow>;
    inferredByReg: Map<string, InferredCapabilities>;
    timestamp?: string;
  } | null>(null);
  const [vehicleListError, setVehicleListError] = useState<string | null>(null);
  const [vehicleListSaving, setVehicleListSaving] = useState(false);
  const [vehicleListSaveError, setVehicleListSaveError] = useState<string | null>(null);
  // Registration → distinct source_systems (from the canonical roster's
  // identifiers), populated on every fetch.
  const [vehicleSourceSystems, setVehicleSourceSystems] = useState<Map<string, Set<string>>>(new Map());
  const [deviceNotes, setDeviceNotes] = useState<Map<string, string>>(new Map());
  const [dataSourceFilter, setDataSourceFilter] = useState<DataSourceFilter>('all');
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: EditableVehicleField } | null>(null);
  const [lastEditedAt, setLastEditedAt] = useState<Date | null>(null);
  const lastFetchedVehicleDetailsRef = useRef<VehicleDetailsRow[]>([]);
  /** Source-system (VehicleObserved) baseline per plate — Observed vs Asserted compare (no declared overlay). */
  const observedVehicleByRegRef = useRef<Map<string, VehicleDetailsRow>>(new Map());
  const [vehicleBaseline, setVehicleBaseline] = useState('[]');
  const [sortColumn, setSortColumn] = useState<SortableVehicleColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [newlyAddedRowIndices, setNewlyAddedRowIndices] = useState<number[]>([]);
  const [newlyAddedRowIndex, setNewlyAddedRowIndex] = useState<number | null>(null);
  const newRowRef = useRef<HTMLTableRowElement | null>(null);
  const [vehicleRegFilter, setVehicleRegFilter] = useState('');
  const [depotNameFilter, setDepotNameFilter] = useState('');
  const [homeDepots, setHomeDepots] = useState<HomeDepotRow[]>([]);
  const [homeDepotsLoading, setHomeDepotsLoading] = useState(false);
  const [homeDepotsError, setHomeDepotsError] = useState<string | null>(null);
  const [homeDepotsSaving, setHomeDepotsSaving] = useState(false);
  const [homeDepotsSaveError, setHomeDepotsSaveError] = useState<string | null>(null);
  const [editingDepotCell, setEditingDepotCell] = useState<{ rowIndex: number; field: EditableDepotField } | null>(null);
  const lastFetchedHomeDepotsRef = useRef<HomeDepotRow[]>([]);
  const [depotBaseline, setDepotBaseline] = useState('[]');
  const [vehicleConfigMeta, setVehicleConfigMeta] = useState<{
    version?: string;
    updatedAt?: string;
  }>({});
  /** Last attribute-save provenance for the footer (not roster refresh time). */
  const [vehicleAttributesMeta, setVehicleAttributesMeta] = useState<{
    updatedAt?: string;
    updatedBy?: string;
  }>({});
  /** Review stamps on Asserted values (green) — Config rate_confirmations analogue. */
  const [fieldConfirmations, setFieldConfirmations] = useState<AssetFieldConfirmations>({});
  /** Suggested equipment per plate (Inferred) — display only, never saved by itself. */
  const [inferredByReg, setInferredByReg] = useState<Map<string, InferredCapabilities>>(new Map());
  /** False until confirmations GET succeeds — Confirm must not wipe server stamps. */
  const [confirmationsLoadedOk, setConfirmationsLoadedOk] = useState(false);
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [depotConfigMeta, setDepotConfigMeta] = useState<{ version?: string; updatedAt?: string }>({});
  const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(new Set());
  const [bulkEditField, setBulkEditField] = useState<BulkEditableVehicleField>('type');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [pendingDeleteVehicleIndex, setPendingDeleteVehicleIndex] = useState<number | null>(null);
  const [pendingDeleteDepotIndex, setPendingDeleteDepotIndex] = useState<number | null>(null);
  const [vehicleEditMode, setVehicleEditMode] = useState(false);
  const [depotEditMode, setDepotEditMode] = useState(false);
  const [showAddVehicleForm, setShowAddVehicleForm] = useState(false);
  const vehicleListFetchInFlightRef = useRef(false);
  const vehicleListFetchGenRef = useRef(0);
  const vehicleDetailsHasChangesRef = useRef(false);
  const [addVehicleForm, setAddVehicleForm] = useState<VehicleDetailsRow>(() => ({
    vehicle_reg: '',
    type: '',
    service_life_yr: '—',
    start_year: '—',
    circuit_name: '',
    fuel: '',
    typical_tonnes: '—',
    unit_cost: '—',
    insurance_yr: '—',
    acquisition: '',
    trailer_deck: '',
    tail_lift: '',
    adr_equipped: '',
    moffett: '',
    small_vehicle: '',
    pallet_spaces: '',
  }));

  // Scheduled reports UI lives on the Profit page (modal) to avoid context switching.

  // Every distinct source_system seen anywhere in the current roster —
  // drives which filter-chip labels are shown at all. Unsaved new rows have
  // no vehicleSourceSystems entry yet but still count as Manual.
  const presentSourceSystems = useMemo(() => {
    const set = new Set<string>();
    for (const systems of vehicleSourceSystems.values()) {
      for (const s of systems) set.add(s);
    }
    if (newlyAddedRowIndices.length > 0) set.add('manual');
    return set;
  }, [vehicleSourceSystems, newlyAddedRowIndices]);

  const sourceFilterCounts = useMemo(() => {
    const counts: Record<NamedVehicleSource, number> & { multiple: number } = {
      samsara: 0,
      aquarius: 0,
      tip: 0,
      supatrak: 0,
      optifleet: 0,
      manual: 0,
      multiple: 0,
    };
    for (const systems of vehicleSourceSystems.values()) {
      for (const key of FILTERABLE_SOURCE_ORDER) {
        if (systems.has(key)) counts[key] += 1;
      }
      if (sourcesForDataSourceBadge(systems).length >= 2) counts.multiple += 1;
    }
    counts.manual += newlyAddedRowIndices.length;
    return counts;
  }, [vehicleSourceSystems, newlyAddedRowIndices]);

  const commitVehicleBaseline = useCallback((rows: VehicleDetailsRow[]) => {
    const copy = rows.map((r) => ({ ...r }));
    lastFetchedVehicleDetailsRef.current = copy;
    setVehicleBaseline(JSON.stringify(copy));
  }, []);

  const commitDepotBaseline = useCallback((rows: HomeDepotRow[]) => {
    const copy = rows.map((r) => ({ ...r }));
    lastFetchedHomeDepotsRef.current = copy;
    setDepotBaseline(JSON.stringify(copy));
  }, []);

  const fetchHomeDepots = useCallback(async () => {
    setHomeDepotsLoading(true);
    setHomeDepotsError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.depots());
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setHomeDepots([]);
        setHomeDepotsError(json.error || `Failed to load home depots (${res.status}).`);
        return;
      }
      const depots = Array.isArray(json.depots) ? json.depots : [];
      const rows: HomeDepotRow[] = depots
        .map((depot: Record<string, unknown>) => ({
          id: String(depot.id ?? '').trim(),
          name: String(depot.name ?? '').trim(),
          depotNumber: String(depot.depotNumber ?? '').trim(),
          postcode: String(depot.postcode ?? '').trim(),
          latitude: Number(depot.latitude),
          longitude: Number(depot.longitude),
          type: String(depot.type ?? 'Depot').trim() || 'Depot',
          // Legacy docs omit the field — treat as shown on map until opted out.
          showOnMap: depot.showOnMap !== false && depot.showOnMap !== 0 && depot.showOnMap !== '0' && depot.showOnMap !== 'false',
          // Missing → not owned. Must be claimed; a partner hub must not count as us.
          owned: depot.owned === true || depot.owned === 1 || depot.owned === '1' || depot.owned === 'true',
        }))
        .filter((depot: HomeDepotRow) => depot.name && Number.isFinite(depot.latitude) && Number.isFinite(depot.longitude))
        .sort((a: HomeDepotRow, b: HomeDepotRow) => a.name.localeCompare(b.name));
      setHomeDepots(rows);
      commitDepotBaseline(rows);
      setDepotConfigMeta({
        version: typeof json.version === 'string' ? json.version : undefined,
        updatedAt: typeof json.updated_at === 'string' ? json.updated_at : undefined,
      });
    } catch (error) {
      setHomeDepots([]);
      setHomeDepotsError(error instanceof Error ? error.message : 'Failed to load home depots.');
      setDepotConfigMeta({});
    } finally {
      setHomeDepotsLoading(false);
    }
  }, [commitDepotBaseline]);

  const homeDepotsHasChanges = useMemo(() => {
    return JSON.stringify(homeDepots) !== depotBaseline;
  }, [homeDepots, depotBaseline]);

  const vehicleDetailsHasChanges = useMemo(() => {
    return JSON.stringify(vehicleDetails) !== vehicleBaseline;
  }, [vehicleDetails, vehicleBaseline]);

  useEffect(() => {
    vehicleDetailsHasChangesRef.current = vehicleDetailsHasChanges;
  }, [vehicleDetailsHasChanges]);

  const configHasUnsavedChanges = vehicleDetailsHasChanges || homeDepotsHasChanges;

  useEffect(() => {
    setUnsavedChangesGuard(() =>
      configHasUnsavedChanges
        ? 'You have unsaved changes on the Configuration page. Leave without saving?'
        : null,
    );
    return () => setUnsavedChangesGuard(null);
  }, [configHasUnsavedChanges]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!configHasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [configHasUnsavedChanges]);

  const isDepotCellEdited = useCallback((row: HomeDepotRow, rowIndex: number, field: EditableDepotField | 'showOnMap' | 'owned') => {
    const original = lastFetchedHomeDepotsRef.current.find((r) => r.id === row.id)
      ?? lastFetchedHomeDepotsRef.current[rowIndex];
    if (!original || original.id !== row.id) {
      return Boolean(row.name.trim());
    }
    if (field === 'showOnMap') {
      return Boolean(row.showOnMap) !== Boolean(original.showOnMap);
    }
    if (field === 'owned') {
      return Boolean(row.owned) !== Boolean(original.owned);
    }
    return String(row[field] ?? '') !== String(original[field] ?? '');
  }, []);

  const handleDepotEdit = (rowIndex: number, field: EditableDepotField, value: string) => {
    setHomeDepots((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      next[rowIndex] = { ...next[rowIndex], [field]: value.trim() };
      return next;
    });
    setEditingDepotCell(null);
  };

  const handleDepotShowOnMapToggle = (rowIndex: number, checked: boolean) => {
    if (!depotEditMode) return;
    setHomeDepots((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      next[rowIndex] = { ...next[rowIndex], showOnMap: checked };
      return next;
    });
  };

  const handleDepotOwnedToggle = (rowIndex: number, checked: boolean) => {
    if (!depotEditMode) return;
    setHomeDepots((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      next[rowIndex] = { ...next[rowIndex], owned: checked };
      return next;
    });
  };

  const handleAddDepot = () => {
    setHomeDepots((prev) => [
      ...prev,
      {
        id: `depot:new:${Date.now()}`,
        name: '',
        depotNumber: '',
        postcode: '',
        // Preserve map compatibility when Lat/Lon are not shown in the UI.
        latitude: 51.5,
        longitude: -0.1,
        type: 'Depot',
        showOnMap: true,
        owned: false,
      },
    ]);
    if (!depotEditMode) setDepotEditMode(true);
  };

  const handleUndoHomeDepots = () => {
    setHomeDepots(lastFetchedHomeDepotsRef.current.map((row) => ({ ...row })));
    setEditingDepotCell(null);
    setHomeDepotsSaveError(null);
  };

  const cancelDepotEdit = () => {
    handleUndoHomeDepots();
    setDepotEditMode(false);
  };

  const revertDepotEdit = () => {
    handleUndoHomeDepots();
  };

  const saveHomeDepots = useCallback(async (): Promise<boolean> => {
    setHomeDepotsSaving(true);
    setHomeDepotsSaveError(null);
    try {
      const depots = homeDepots
        .filter((row) => row.name.trim() && Number.isFinite(row.latitude) && Number.isFinite(row.longitude))
        .map((row) => ({
          id: row.id || `depot:${row.name.toLowerCase().replace(/\s+/g, '_')}`,
          name: row.name.trim(),
          depotNumber: row.depotNumber.trim() || null,
          postcode: row.postcode.trim() || undefined,
          latitude: row.latitude,
          longitude: row.longitude,
          type: row.type.trim() || 'Depot',
          showOnMap: Boolean(row.showOnMap),
          owned: Boolean(row.owned),
        }));

      const ownedWithoutNumber = depots.filter((row) => row.owned && !row.depotNumber);
      if (ownedWithoutNumber.length > 0) {
        throw new Error(
          'Owned depots need a Palletways depot number (e.g. 102). '
            + ownedWithoutNumber.map((row) => row.name).join(', ')
            + (ownedWithoutNumber.length === 1 ? ' has' : ' have')
            + ' Owned ticked but no number.',
        );
      }

      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.config.depots(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ depots }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success !== true) {
        throw new Error(json.error || json.message || `Failed to save depots (${res.status})`);
      }
      commitDepotBaseline(homeDepots);
      setDepotConfigMeta((prev) => ({
        version: typeof json.version === 'string' ? json.version : prev.version,
        updatedAt: typeof json.updated_at === 'string' ? json.updated_at : prev.updatedAt,
      }));
      setDepotEditMode(false);
      return true;
    } catch (err) {
      setHomeDepotsSaveError(err instanceof Error ? err.message : 'Failed to save home depots');
      return false;
    } finally {
      setHomeDepotsSaving(false);
    }
  }, [homeDepots, commitDepotBaseline]);

  const fetchVehicleList = useCallback(async (opts?: { fresh?: boolean }) => {
    if (vehicleListFetchInFlightRef.current) return;
    const fresh = opts?.fresh === true;
    vehicleListFetchInFlightRef.current = true;
    const fetchGen = ++vehicleListFetchGenRef.current;
    setVehicleListLoading(true);
    setVehicleListEnriching(false);
    setPendingEnrichedRoster(null);
    setVehicleListError(null);
    setSelectedRowIndices(new Set());

    const applyMapped = (mapped: {
      rows: VehicleDetailsRow[];
      sources: Map<string, Set<string>>;
      observedByReg: Map<string, VehicleDetailsRow>;
      inferredByReg: Map<string, InferredCapabilities>;
      timestamp?: string;
    }) => {
      setVehicleSourceSystems(mapped.sources);
      observedVehicleByRegRef.current = mapped.observedByReg;
      setInferredByReg(mapped.inferredByReg);
      setVehicleDetails(mapped.rows);
      commitVehicleBaseline(mapped.rows);
      setLastEditedAt(null);
      setNewlyAddedRowIndices([]);
      setVehicleListSaveError(null);
      setPendingEnrichedRoster(null);
      setVehicleConfigMeta(
        typeof mapped.timestamp === 'string' && mapped.timestamp
          ? { updatedAt: mapped.timestamp }
          : {},
      );
    };

    const applyPayload = (json: {
      vehicles: VehiclesListApiItem[];
      timestamp?: string;
    }) => {
      const mapped = mapVehiclesListToDetails(json.vehicles);
      setDeviceNotes(samsaraDeviceNotesByReg(json.vehicles));
      applyMapped({
        ...mapped,
        timestamp: typeof json.timestamp === 'string' ? json.timestamp : undefined,
      });
    };

    const loadSideChannels = () => {
      void fetchConfigAssumptionsHistory('vehicle-attributes')
        .then((hist) => {
          if (fetchGen !== vehicleListFetchGenRef.current) return;
          const latest = hist.entries?.[0];
          if (!latest) return;
          setVehicleAttributesMeta({
            updatedAt: latest.saved_at,
            updatedBy: latest.saved_by,
          });
        })
        .catch(() => {
          /* history optional */
        });
      void authenticatedFetch(API_ENDPOINTS.fleetIntelligence.vehicles.attributesConfirmations())
        .then(async (res) => {
          if (fetchGen !== vehicleListFetchGenRef.current) return;
          if (!res.ok) {
            setConfirmationsLoadedOk(false);
            return;
          }
          const doc = (await res.json().catch(() => ({}))) as { confirmations?: AssetFieldConfirmations };
          if (doc.confirmations && typeof doc.confirmations === 'object') {
            setFieldConfirmations(doc.confirmations);
          } else {
            setFieldConfirmations({});
          }
          setConfirmationsLoadedOk(true);
        })
        .catch(() => {
          if (fetchGen !== vehicleListFetchGenRef.current) return;
          setConfirmationsLoadedOk(false);
        });
    };

    const fetchList = async (enrichment?: 'skip') => {
      const res = await authenticatedFetch(
        API_ENDPOINTS.fleetIntelligence.vehicles.list(undefined, {
          ...(enrichment === 'skip' ? { enrichment: 'skip' as const } : {}),
          ...(fresh ? { refresh: true } : {}),
        }),
      );
      const json = await res.json().catch(() => ({}));
      return { res, json };
    };

    try {
      // Last full roster this session: paint it at once, then revalidate.
      let painted = false;
      const sessionCopy = fresh ? null : readAssetsRosterSessionCopy<VehiclesListApiItem>();
      if (sessionCopy) {
        applyPayload(sessionCopy);
        setObservedBaselineReady(true);
        painted = true;
        setVehicleListLoading(false);
        loadSideChannels();
      }

      // Phase 1: entity + declared only — paint before VehicleObserved Trino.
      if (!painted) {
      try {
        const { res, json } = await fetchList('skip');
        if (fetchGen !== vehicleListFetchGenRef.current) return;
        if (res.ok && Array.isArray(json.vehicles)) {
          applyPayload(json);
          setObservedBaselineReady(false);
          painted = true;
          setVehicleListLoading(false);
          loadSideChannels();
        }
      } catch {
        /* fall through to full list */
      }
      }

      // Phase 2: full roster with VehicleObserved (Aquarius gate + type/year fallbacks).
      setVehicleListEnriching(true);
      try {
        const { res, json } = await fetchList();
        if (fetchGen !== vehicleListFetchGenRef.current) return;
        if (!res.ok || !Array.isArray(json.vehicles)) {
          if (!painted) {
            setVehicleListError(json.error || `Failed to load vehicle list (${res.status}).`);
            setVehicleDetails([]);
            setVehicleSourceSystems(new Map());
            setVehicleConfigMeta({});
            setVehicleAttributesMeta({});
            setFieldConfirmations({});
            setConfirmationsLoadedOk(false);
            observedVehicleByRegRef.current = new Map();
            setInferredByReg(new Map());
          } else {
            setVehicleListError(
              json.error || 'Roster enrichment failed — showing declared attributes only.',
            );
          }
          return;
        }
        writeAssetsRosterSessionCopy(
          json.vehicles as VehiclesListApiItem[],
          typeof json.timestamp === 'string' ? json.timestamp : undefined,
        );
        const mapped = mapVehiclesListToDetails(json.vehicles as VehiclesListApiItem[]);
        setDeviceNotes(samsaraDeviceNotesByReg(json.vehicles as VehiclesListApiItem[]));
        const mappedWithTs = {
          ...mapped,
          timestamp: typeof json.timestamp === 'string' ? json.timestamp : undefined,
        };
        if (decideEnrichedRosterApply(vehicleDetailsHasChangesRef.current) === 'defer') {
          setPendingEnrichedRoster(mappedWithTs);
        } else {
          applyMapped(mappedWithTs);
          setObservedBaselineReady(true);
          if (!painted) loadSideChannels();
        }
        setVehicleListError(null);
      } catch (err) {
        if (fetchGen !== vehicleListFetchGenRef.current) return;
        if (!painted) {
          setVehicleListError(err instanceof Error ? err.message : 'Failed to fetch vehicle list');
          setVehicleConfigMeta({});
          setVehicleAttributesMeta({});
          setFieldConfirmations({});
          setConfirmationsLoadedOk(false);
          observedVehicleByRegRef.current = new Map();
          setInferredByReg(new Map());
        } else {
          setVehicleListError(
            err instanceof Error
              ? err.message
              : 'Roster enrichment failed — showing declared attributes only.',
          );
        }
      } finally {
        if (fetchGen === vehicleListFetchGenRef.current) {
          setVehicleListEnriching(false);
        }
      }
    } finally {
      if (fetchGen === vehicleListFetchGenRef.current) {
        setVehicleListLoading(false);
        vehicleListFetchInFlightRef.current = false;
      }
    }
  }, [commitVehicleBaseline]);

  const applyPendingEnrichedRoster = useCallback(() => {
    if (!pendingEnrichedRoster) return;
    setObservedBaselineReady(true);
    setVehicleSourceSystems(pendingEnrichedRoster.sources);
    observedVehicleByRegRef.current = pendingEnrichedRoster.observedByReg;
    setInferredByReg(pendingEnrichedRoster.inferredByReg);
    setVehicleDetails(pendingEnrichedRoster.rows);
    commitVehicleBaseline(pendingEnrichedRoster.rows);
    setLastEditedAt(null);
    setNewlyAddedRowIndices([]);
    setVehicleListSaveError(null);
    setPendingEnrichedRoster(null);
    setVehicleEditMode(false);
    setSelectedRowIndices(new Set());
    setVehicleConfigMeta(
      typeof pendingEnrichedRoster.timestamp === 'string' && pendingEnrichedRoster.timestamp
        ? { updatedAt: pendingEnrichedRoster.timestamp }
        : {},
    );
  }, [pendingEnrichedRoster, commitVehicleBaseline]);

  const rosterStatusBanner = progressiveRosterBanner({
    enriching: vehicleListEnriching,
    declaredCount: vehicleDetails.length,
    pendingFullCount: pendingEnrichedRoster ? pendingEnrichedRoster.rows.length : null,
  });

  const DECLARED_FIELD_KEYS: Array<keyof VehicleDetailsRow> = [
    'type', 'service_life_yr', 'start_year', 'circuit_name', 'fuel',
    'typical_tonnes', 'unit_cost', 'insurance_yr', 'acquisition', 'trailer_deck',
    'tail_lift', 'adr_equipped', 'moffett', 'small_vehicle', 'pallet_spaces', 'metric', 'metric_range_tonnes',
  ];

  const saveVehicleAttributes = useCallback(async () => {
    // Each vehicle's attributes are a whole-snapshot Truth Layer event
    // (VehicleAttributesDeclared) — only submit rows that actually changed
    // since the last fetch (or are new/deleted) rather than re-declaring
    // every unchanged vehicle on every save.
    const baseline = lastFetchedVehicleDetailsRef.current;
    const baselineByReg = new Map(baseline.map((r) => [String(r.vehicle_reg ?? '').trim(), r]));
    const currentRegs = new Set(vehicleDetails.map((r) => String(r.vehicle_reg ?? '').trim()).filter(Boolean));
    const clean = (v: unknown): string | undefined => {
      const s = String(v ?? '').trim();
      return !s || s === '—' ? undefined : s;
    };

    const updates: Record<string, unknown>[] = [];

    vehicleDetails.forEach((row, idx) => {
      const reg = String(row.vehicle_reg ?? '').trim();
      if (!reg) return;
      const original = baselineByReg.get(reg);
      const isNew = newlyAddedRowIndices.includes(idx);
      const changedFields: string[] = [];
      for (const f of DECLARED_FIELD_KEYS) {
        const cur = String(row[f] ?? '').trim();
        const prev = original ? String(original[f] ?? '').trim() : '';
        if (isNew) {
          if (clean(row[f])) changedFields.push(String(f));
        } else if (cur !== prev) {
          changedFields.push(String(f));
        }
      }
      if (!isNew && changedFields.length === 0) return;

      const patch: Record<string, unknown> = {
        registration: reg,
        status: 'active',
        changed_fields: changedFields,
      };
      for (const f of changedFields) {
        patch[f] = clean(row[f as keyof VehicleDetailsRow]);
      }
      updates.push(patch);
    });

    // Rows present at last fetch but no longer in the table were deleted —
    // declare them excluded so they drop out of the roster. Nothing is
    // actually deleted: the prior events remain, visible via the Truth
    // Browser Claims View (Constitution §1.1/§5).
    for (const reg of baselineByReg.keys()) {
      if (!currentRegs.has(reg)) {
        updates.push({
          registration: reg,
          status: 'excluded',
          changed_fields: ['status'],
        });
      }
    }

    if (updates.length === 0) return;

    const actor =
      readSessionUserEmail()
      || (await getCurrentUserEmail())?.trim()
      || 'user';

    const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.vehicles.attributes(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, updated_by: actor }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success !== true) {
      throw new Error(json.error || json.message || `Failed to save vehicle attributes (${res.status})`);
    }
    // The saved roster differs from the session copy.
    clearAssetsRosterSessionCopy();
    clearFleetCostAssetClassLabelsCache();

    if (typeof json.updated_at === 'string' || typeof json.updated_by === 'string') {
      setVehicleAttributesMeta({
        updatedAt: typeof json.updated_at === 'string' ? json.updated_at : undefined,
        updatedBy: typeof json.updated_by === 'string' ? json.updated_by : actor,
      });
    } else {
      setVehicleAttributesMeta({ updatedAt: new Date().toISOString(), updatedBy: actor });
    }
  }, [vehicleDetails, newlyAddedRowIndices]);

  const saveVehicleList = useCallback(async (): Promise<boolean> => {
    setVehicleListSaving(true);
    setVehicleListSaveError(null);
    try {
      await saveVehicleAttributes();
      commitVehicleBaseline(vehicleDetails);
      setLastEditedAt(null);
      setNewlyAddedRowIndices([]);
      setVehicleEditMode(false);
      return true;
    } catch (err) {
      setVehicleListSaveError(err instanceof Error ? err.message : 'Failed to save vehicle list');
      return false;
    } finally {
      setVehicleListSaving(false);
    }
  }, [vehicleDetails, saveVehicleAttributes, commitVehicleBaseline]);

  useEffect(() => {
    fetchVehicleList();
    fetchHomeDepots();
  }, [fetchVehicleList, fetchHomeDepots]);

  const handleVehicleDetailEdit = (rowIndex: number, field: keyof VehicleDetailsRow, value: string) => {
    const normalizedValue =
      field === 'typical_tonnes'
        ? formatTypicalTonnes(value)
        : field === 'service_life_yr'
          ? formatServiceLifeYr(value)
          : field === 'start_year'
            ? formatStartYear(value)
            : field === 'unit_cost' || field === 'insurance_yr'
              ? formatMoneyGbp(value)
              : field === 'acquisition'
                ? formatAcquisition(value)
                : field === 'trailer_deck'
                  ? formatTrailerDeck(value)
                  : isYesNoCapabilityField(String(field))
                    ? formatYesNo(value)
                    : field === 'pallet_spaces'
                      ? formatPalletSpaces(value)
                      : value;
    const bulkTargets =
      field !== 'vehicle_reg' && selectedRowIndices.size > 1 && selectedRowIndices.has(rowIndex)
        ? [...selectedRowIndices]
        : [rowIndex];

    setVehicleDetails((prev) => {
      const next = [...prev];
      for (const idx of bulkTargets) {
        if (next[idx]) next[idx] = { ...next[idx], [field]: normalizedValue };
      }
      return next;
    });
    setLastEditedAt(new Date());
    setEditingCell(null);
  };

  const defaultVehicleRow = (): VehicleDetailsRow => ({
    vehicle_reg: '',
    type: '',
    service_life_yr: '—',
    start_year: '—',
    circuit_name: '',
    fuel: '',
    typical_tonnes: '—',
    unit_cost: '—',
    insurance_yr: '—',
    acquisition: '',
    trailer_deck: '',
    tail_lift: '',
    adr_equipped: '',
    moffett: '',
    small_vehicle: '',
    pallet_spaces: '',
  });

  const restoreVehicleBaseline = () => {
    setVehicleDetails(lastFetchedVehicleDetailsRef.current.map((r) => ({ ...r })));
    setLastEditedAt(null);
    setEditingCell(null);
    setNewlyAddedRowIndices([]);
    setNewlyAddedRowIndex(null);
    setSelectedRowIndices(new Set());
    setBulkEditValue('');
    setVehicleListSaveError(null);
  };

  /** Discard unsaved edits and leave edit mode (Config Cancel). */
  const cancelVehicleEdit = () => {
    restoreVehicleBaseline();
    setVehicleEditMode(false);
    if (pendingEnrichedRoster) {
      // Edits discarded — safe to take the deferred full roster.
      setObservedBaselineReady(true);
      setVehicleSourceSystems(pendingEnrichedRoster.sources);
      observedVehicleByRegRef.current = pendingEnrichedRoster.observedByReg;
      setInferredByReg(pendingEnrichedRoster.inferredByReg);
      setVehicleDetails(pendingEnrichedRoster.rows);
      commitVehicleBaseline(pendingEnrichedRoster.rows);
      setPendingEnrichedRoster(null);
      setVehicleConfigMeta(
        typeof pendingEnrichedRoster.timestamp === 'string' && pendingEnrichedRoster.timestamp
          ? { updatedAt: pendingEnrichedRoster.timestamp }
          : {},
      );
    }
  };

  /** Restore last saved version; stay in edit mode (Config Revert). */
  const revertVehicleEdit = () => {
    restoreVehicleBaseline();
  };

  /**
   * Reset editable fields to telematics / blank estimates; stay in edit mode
   * (Config Reset → defaults). Nothing persists until Save.
   */
  const resetVehicleEdit = () => {
    setVehicleDetails((prev) =>
      prev.map((row, idx) => {
        const reg = String(row.vehicle_reg ?? '').trim();
        if (newlyAddedRowIndices.includes(idx)) {
          return normalizeVehicleRow({
            ...defaultVehicleRow(),
            vehicle_reg: reg,
          });
        }
        const observed = observedVehicleByRegRef.current.get(reg);
        if (observed) return { ...observed, vehicle_reg: reg || String(observed.vehicle_reg ?? '') };
        return normalizeVehicleRow({
          ...defaultVehicleRow(),
          vehicle_reg: reg,
        });
      }),
    );
    setLastEditedAt(new Date());
    setEditingCell(null);
    setVehicleListSaveError(null);
  };

  const requestRemoveVehicle = (originalIndex: number) => {
    if (!vehicleEditMode) return;
    setPendingDeleteVehicleIndex(originalIndex);
    setConfirmAction('delete-vehicle');
  };

  const handleConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'delete-vehicle' && pendingDeleteVehicleIndex != null) {
      handleRemoveVehicle(pendingDeleteVehicleIndex);
      setPendingDeleteVehicleIndex(null);
      return;
    }
    if (action === 'delete-depot' && pendingDeleteDepotIndex != null) {
      setHomeDepots((prev) => prev.filter((_, i) => i !== pendingDeleteDepotIndex));
      setEditingDepotCell(null);
      setPendingDeleteDepotIndex(null);
      return;
    }
    if (action === 'refresh-vehicles') {
      await fetchVehicleList({ fresh: true });
      setVehicleEditMode(false);
      return;
    }
    if (action === 'refresh-depots') {
      await fetchHomeDepots();
      setDepotEditMode(false);
      return;
    }
  };

  const handleVehicleEditAllOrSave = async () => {
    if (!vehicleEditMode) {
      setVehicleEditMode(true);
      return;
    }
    if (vehicleDetailsHasChanges) {
      await saveVehicleList();
      return;
    }
    setVehicleEditMode(false);
    setEditingCell(null);
  };

  const handleDepotEditAllOrSave = async () => {
    if (!depotEditMode) {
      setDepotEditMode(true);
      return;
    }
    if (homeDepotsHasChanges) {
      await saveHomeDepots();
      return;
    }
    setDepotEditMode(false);
    setEditingDepotCell(null);
  };

  const openAddVehicleForm = () => {
    setAddVehicleForm(defaultVehicleRow());
    setShowAddVehicleForm(true);
  };

  const handleAddVehicleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const row: VehicleDetailsRow = normalizeVehicleRow({
      vehicle_reg: String(addVehicleForm.vehicle_reg ?? '').trim(),
      type: String(addVehicleForm.type ?? '').trim(),
      service_life_yr: String(addVehicleForm.service_life_yr ?? '—').trim() || '—',
      start_year: String(addVehicleForm.start_year ?? '—').trim() || '—',
      circuit_name: String(addVehicleForm.circuit_name ?? '').trim(),
      fuel: String(addVehicleForm.fuel ?? '').trim(),
      typical_tonnes: String(addVehicleForm.typical_tonnes ?? '—').trim() || '—',
      unit_cost: String(addVehicleForm.unit_cost ?? '—').trim() || '—',
      insurance_yr: String(addVehicleForm.insurance_yr ?? '—').trim() || '—',
    });
    const newIndex = vehicleDetails.length;
    setVehicleDetails((prev) => [...prev, row]);
    setShowAddVehicleForm(false);
    setLastEditedAt(new Date());
    setNewlyAddedRowIndices((prev) => [...prev, newIndex]);
    setNewlyAddedRowIndex(newIndex);
  };

  useEffect(() => {
    if (newlyAddedRowIndex !== null && newRowRef.current) {
      newRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setNewlyAddedRowIndex(null);
    }
  }, [newlyAddedRowIndex]);

  const handleRemoveVehicle = (originalIndex: number) => {
    setVehicleDetails((prev) => prev.filter((_, i) => i !== originalIndex));
    setSelectedRowIndices((prev) => {
      const next = new Set<number>();
      for (const idx of prev) {
        if (idx === originalIndex) continue;
        next.add(idx > originalIndex ? idx - 1 : idx);
      }
      return next;
    });
    setNewlyAddedRowIndices((prev) =>
      prev.filter((i) => i !== originalIndex).map((i) => (i > originalIndex ? i - 1 : i))
    );
    setLastEditedAt(new Date());
    setEditingCell(null);
  };

  /** §13.2 provenance class of a cell (Observed / Asserted / Unknown), plus the confirmed review stamp. */
  const cellFieldStatus = (row: VehicleDetailsRow, field: keyof VehicleDetailsRow) => {
    const reg = String(row.vehicle_reg ?? '').trim();
    const observed = observedVehicleByRegRef.current.get(reg);
    return resolveAssetFieldStatus(
      row[field],
      observed?.[field],
      fieldConfirmations[assetConfirmKey(reg, String(field))],
    );
  };

  const cellFieldClass = (
    row: VehicleDetailsRow,
    _originalIndex: number,
    field: keyof VehicleDetailsRow,
  ): string => assetFieldTextClass(cellFieldStatus(row, field));

  const cellFieldTitle = (row: VehicleDetailsRow, field: keyof VehicleDetailsRow): string =>
    assetFieldStatusTitle(cellFieldStatus(row, field));

  const cellInputClass = (
    row: VehicleDetailsRow,
    field: keyof VehicleDetailsRow,
    baseInputClass: string,
  ): string => assetFieldInputClass(cellFieldStatus(row, field), baseInputClass);

  /**
   * Tail-lift / ADR / Moffett / Small vehicle cell. An empty cell may show a
   * suggestion (Inferred, purple italic): never saved unless someone fills it in.
   */
  const renderYesNoCapabilityCell = (
    row: VehicleDetailsRow,
    originalIndex: number,
    field: YesNoCapabilityField,
    editing: boolean,
    cellMinHeight: string,
    baseInputClass: string,
  ) => {
    const value = formatYesNo(row[field]);
    const suggestion = value ? undefined : suggestionFor(inferredByReg.get(String(row.vehicle_reg ?? '').trim()), field);
    const textClass = suggestion?.value ? 'text-purple-300 italic' : cellFieldClass(row, originalIndex, field);
    return (
      <td
        key={field}
        className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${textClass}`}
        title={suggestion ? suggestionTitle(field, suggestion) : cellFieldTitle(row, field)}
        onClick={() => vehicleEditMode && !editing && setEditingCell({ rowIndex: originalIndex, field })}
      >
        {editing ? (
          <select
            defaultValue={value}
            onChange={(e) => handleVehicleDetailEdit(originalIndex, field, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditingCell(null);
            }}
            onClick={(e) => e.stopPropagation()}
            className={cellInputClass(row, field, baseInputClass)}
            aria-label={`${CAPABILITY_FIELD_LABELS[field]} for ${String(row.vehicle_reg ?? '')}`}
            autoFocus
          >
            <option value="">Not set</option>
            {YES_NO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {suggestion?.value === o.value ? ' (suggested)' : ''}
              </option>
            ))}
          </select>
        ) : value ? (
          <span className="inline-block truncate">{yesNoLabel(value)}</span>
        ) : suggestion?.value ? (
          <span className="inline-block truncate">{yesNoLabel(suggestion.value)}?</span>
        ) : (
          <span className="inline-block truncate">{UNKNOWN_CELL}</span>
        )}
      </td>
    );
  };

  const renderPalletSpacesCell = (
    row: VehicleDetailsRow,
    originalIndex: number,
    editing: boolean,
    cellMinHeight: string,
    baseInputClass: string,
  ) => {
    const commit = (raw: string) => {
      handleVehicleDetailEdit(originalIndex, 'pallet_spaces', raw);
      setEditingCell(null);
    };
    const shown = formatPalletSpaces(row.pallet_spaces);
    const suggestion = shown ? undefined : suggestionFor(inferredByReg.get(String(row.vehicle_reg ?? '').trim()), 'pallet_spaces');
    return (
      <td
        className={`py-2 px-4 text-right ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${suggestion?.value ? 'text-purple-300 italic' : cellFieldClass(row, originalIndex, 'pallet_spaces')}`}
        title={suggestion ? suggestionTitle('pallet_spaces', suggestion) : cellFieldTitle(row, 'pallet_spaces')}
        onClick={() => vehicleEditMode && !editing && setEditingCell({ rowIndex: originalIndex, field: 'pallet_spaces' })}
      >
        {editing ? (
          <input
            type="text"
            inputMode="numeric"
            defaultValue={shown}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditingCell(null);
              if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
            }}
            onClick={(e) => e.stopPropagation()}
            className={`${cellInputClass(row, 'pallet_spaces', baseInputClass)} text-right w-16`}
            aria-label={`Pallet spaces for ${String(row.vehicle_reg ?? '')}`}
            autoFocus
          />
        ) : (
          <span className="inline-block truncate">{shown || (suggestion?.value ? `${suggestion.value}?` : UNKNOWN_CELL)}</span>
        )}
      </td>
    );
  };

  /**
   * Copy suggested tail-lift / ADR values into empty cells (selected rows, or
   * every row when none is selected). They become the user's Asserted edits
   * and are saved only when the user saves.
   */
  const fillInSuggestions = () => {
    const targets = selectedRowIndices.size > 0 ? [...selectedRowIndices] : vehicleDetails.map((_, i) => i);
    const { rows, filled } = fillSuggestedCapabilities(vehicleDetails, targets, inferredByReg);
    if (filled === 0) return;
    setVehicleDetails(rows);
    setLastEditedAt(new Date());
  };

  const canFillSuggestions = useMemo(
    () => inferredByReg.size > 0 && hasFillableSuggestions(vehicleDetails, inferredByReg),
    [vehicleDetails, inferredByReg],
  );

  /** Asserted field values on the saved roster (eligible for Confirm). */
  const collectAssertedOverrides = useCallback((): AssetFieldConfirmations => {
    return collectAssertedAssetFieldOverrides({
      rows: lastFetchedVehicleDetailsRef.current,
      observedByReg: observedVehicleByRegRef.current,
      fields: CONFIRMABLE_VEHICLE_FIELDS,
    });
  }, []);

  /**
   * Confirm needs the full roster: until VehicleObserved enrichment lands (or
   * if it failed) the Observed baseline is empty, so every declared value would
   * read as Asserted.
   */
  const rosterAwaitingObserved = !observedBaselineReady || pendingEnrichedRoster != null;

  const hasConfirmableOverrides = useMemo(() => {
    if (vehicleEditMode || !confirmationsLoadedOk || rosterAwaitingObserved) return false;
    return hasUnconfirmedAssertedOverrides({
      overrides: collectAssertedOverrides(),
      confirmations: fieldConfirmations,
    });
  }, [
    collectAssertedOverrides,
    confirmationsLoadedOk,
    fieldConfirmations,
    rosterAwaitingObserved,
    vehicleEditMode,
    vehicleBaseline,
  ]);

  const confirmAssetValues = async () => {
    if (vehicleEditMode) {
      setVehicleListSaveError('Save or cancel your edits before confirming values.');
      return;
    }
    if (!confirmationsLoadedOk) {
      setVehicleListSaveError('Confirmations have not loaded — refresh Assets before confirming.');
      return;
    }
    if (rosterAwaitingObserved) {
      setVehicleListSaveError('Source-system values have not loaded — confirm once the roster has finished (or refresh).');
      return;
    }
    const overrides = collectAssertedOverrides();
    if (!hasUnconfirmedAssertedOverrides({ overrides, confirmations: fieldConfirmations })) {
      setVehicleListSaveError('Nothing to confirm — no Asserted values differ from their last confirmation.');
      return;
    }

    setConfirmSaving(true);
    setVehicleListSaveError(null);
    try {
      const actor =
        readSessionUserEmail()
        || (await getCurrentUserEmail())?.trim()
        || 'user';
      const res = await authenticatedFetch(
        API_ENDPOINTS.fleetIntelligence.vehicles.attributesConfirmations(),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ overrides_to_confirm: overrides, updated_by: actor }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success !== true) {
        throw new Error(json.error || json.message || `Failed to confirm values (${res.status})`);
      }
      const saved =
        json.confirmations && typeof json.confirmations === 'object'
          ? (json.confirmations as AssetFieldConfirmations)
          : {};
      setFieldConfirmations(saved);
      setConfirmationsLoadedOk(true);
    } catch (err) {
      setVehicleListSaveError(err instanceof Error ? err.message : 'Failed to confirm values');
    } finally {
      setConfirmSaving(false);
    }
  };

  const handleSort = (columnKey: SortableVehicleColumn) => {
    if (sortColumn === columnKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return vehicleDetails.map((row, i) => ({ row, originalIndex: i }));
    }
    const sorted = [...vehicleDetails]
      .map((row, i) => ({ row, originalIndex: i }))
      .sort((a, b) => {
        const sortValue = (item: { row: VehicleDetailsRow; originalIndex: number }): string => {
          if (sortColumn === 'data_source') {
            const isNew = newlyAddedRowIndices.includes(item.originalIndex);
            const reg = String(item.row.vehicle_reg ?? '').trim();
            const named = isNew
              ? (['manual'] as NamedVehicleSource[])
              : sourcesForDataSourceBadge(vehicleSourceSystems.get(reg));
            return dataSourceBadgeLabel(named, isNew).toLowerCase();
          }
          return String(item.row[sortColumn] ?? '').toLowerCase();
        };
        const aStr = sortValue(a);
        const bStr = sortValue(b);
        const aNum = parseFloat(aStr);
        const bNum = parseFloat(bStr);
        const numeric =
          sortColumn !== 'data_source'
          && !Number.isNaN(aNum)
          && !Number.isNaN(bNum);
        if (numeric) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
        const cmp = aStr.localeCompare(bStr);
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    return sorted;
  }, [vehicleDetails, sortColumn, sortDirection, vehicleSourceSystems, newlyAddedRowIndices]);

  const filteredRows = useMemo(() => {
    let rows = sortedRows;
    if (vehicleRegFilter.trim()) {
      const q = vehicleRegFilter.trim().toLowerCase();
      rows = rows.filter(({ row }) =>
        String(row.vehicle_reg ?? '').toLowerCase().includes(q)
      );
    }
    if (dataSourceFilter !== 'all') {
      rows = rows.filter(({ row, originalIndex }) => {
        const reg = String(row.vehicle_reg ?? '').trim();
        const systems = vehicleSourceSystems.get(reg);
        if (dataSourceFilter === 'multiple') {
          return sourcesForDataSourceBadge(systems).length >= 2;
        }
        if (dataSourceFilter === 'manual') {
          // Unsaved new rows have no vehicleSourceSystems entry yet but are Manual.
          return systems?.has('manual') === true || newlyAddedRowIndices.includes(originalIndex);
        }
        // Membership: every vehicle claimed by this source (incl. multi-source).
        return systems?.has(dataSourceFilter) === true;
      });
    }
    return rows;
  }, [sortedRows, vehicleRegFilter, dataSourceFilter, vehicleSourceSystems, newlyAddedRowIndices]);

  const allFilteredSelected =
    filteredRows.length > 0 && filteredRows.every(({ originalIndex }) => selectedRowIndices.has(originalIndex));

  const toggleRowSelection = (originalIndex: number) => {
    setSelectedRowIndices((prev) => {
      const next = new Set(prev);
      if (next.has(originalIndex)) next.delete(originalIndex);
      else next.add(originalIndex);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedRowIndices((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredRows.forEach(({ originalIndex }) => next.delete(originalIndex));
      } else {
        filteredRows.forEach(({ originalIndex }) => next.add(originalIndex));
      }
      return next;
    });
  };

  const clearRowSelection = () => {
    setSelectedRowIndices(new Set());
    setBulkEditValue('');
  };

  const handleBulkApply = () => {
    if (selectedRowIndices.size === 0) return;
    const raw = bulkEditValue.trim();
    const value =
      bulkEditField === 'typical_tonnes'
        ? formatTypicalTonnes(raw)
        : bulkEditField === 'service_life_yr'
          ? formatServiceLifeYr(raw)
          : bulkEditField === 'start_year'
            ? formatStartYear(raw)
            : bulkEditField === 'unit_cost' || bulkEditField === 'insurance_yr'
              ? formatMoneyGbp(raw)
              : bulkEditField === 'acquisition'
                ? formatAcquisition(raw)
                : bulkEditField === 'trailer_deck'
                  ? formatTrailerDeck(raw)
                  : isYesNoCapabilityField(bulkEditField)
                    ? formatYesNo(raw)
                    : bulkEditField === 'pallet_spaces'
                      ? formatPalletSpaces(raw)
                      : raw || '—';

    setVehicleDetails((prev) => {
      const next = [...prev];
      for (const idx of selectedRowIndices) {
        // Deck means nothing for a vehicle: only selected trailers take it.
        if (bulkEditField === 'trailer_deck' && next[idx] && !isTrailerRow(next[idx])) continue;
        if (next[idx]) next[idx] = { ...next[idx], [bulkEditField]: value };
      }
      return next;
    });
    setLastEditedAt(new Date());
    setBulkEditValue('');
  };

  const vehicleRegOptions = useMemo(
    () => [...new Set(vehicleDetails.map((r) => String(r.vehicle_reg ?? '').trim()).filter(Boolean))].sort(),
    [vehicleDetails]
  );

  const depotNameOptions = useMemo(
    () => [...new Set(homeDepots.map((r) => String(r.name ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [homeDepots],
  );

  const filteredDepots = useMemo(() => {
    const rows = homeDepots.map((row, originalIndex) => ({ row, originalIndex }));
    if (!depotNameFilter.trim()) return rows;
    const q = depotNameFilter.trim().toLowerCase();
    return rows.filter(({ row }) => {
      const name = String(row.name ?? '').toLowerCase();
      const number = String(row.depotNumber ?? '').toLowerCase();
      const postcode = String(row.postcode ?? '').toLowerCase();
      return name.includes(q) || number.includes(q) || postcode.includes(q);
    });
  }, [homeDepots, depotNameFilter]);

  const renderSortableHeader = (columnKey: SortableVehicleColumn, label: string | ReactNode, align: 'left' | 'right' = 'left') => {
    const isActive = sortColumn === columnKey;
    return (
      <Button
        variant="ghost" size="md" type="button"
        className={`w-full h-auto justify-start px-4 py-3.5 bg-card text-foreground hover:text-success ${align === 'right' ? 'text-right' : 'text-left'}`}
        onClick={() => handleSort(columnKey)}
        title="Click to sort"
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
          {label}
          {isActive && (
            sortDirection === 'asc' ? (
              <ChevronUp aria-hidden className="w-4 h-4 flex-shrink-0 text-success" />
            ) : (
              <ChevronDown aria-hidden className="w-4 h-4 flex-shrink-0 text-success" />
            )
          )}
        </div>
      </Button>
    );
  };

  const renderDataSourceBadge = (vehicleReg: string, isNewRow: boolean): React.ReactNode => {
    const named = isNewRow
      ? (['manual'] as NamedVehicleSource[])
      : sourcesForDataSourceBadge(vehicleSourceSystems.get(String(vehicleReg || '').trim()));
    const label = dataSourceBadgeLabel(named, isNewRow);
    // Unsaved new rows pulse so they stay visually distinct from vehicles
    // that were already manually declared in a prior session — both would
    // otherwise render as an identical static "Manual" badge.
    const title = isNewRow ? 'Manually added vehicle (unsaved)' : label;
    return (
      <span
        className={`inline-flex max-w-full items-center truncate rounded-md border px-2 py-0.5 text-xs font-medium ${dataSourceBadgeClass(named, isNewRow)} ${isNewRow ? 'animate-pulse' : ''}`}
        title={title}
      >
        {label}
      </span>
    );
  };

  const filterableSourcesPresent = FILTERABLE_SOURCE_ORDER.filter((s) =>
    presentSourceSystems.has(s),
  );
  const hasMultiSourceVehicles = sourceFilterCounts.multiple > 0;

  // Guard: if a filter becomes unavailable after tenant scoping, reset to All.
  useEffect(() => {
    if (dataSourceFilter === 'all') return;
    if (dataSourceFilter === 'multiple') {
      if (!hasMultiSourceVehicles) setDataSourceFilter('all');
      return;
    }
    if (!presentSourceSystems.has(dataSourceFilter)) setDataSourceFilter('all');
  }, [dataSourceFilter, presentSourceSystems, hasMultiSourceVehicles]);

  const payloadOptions = useMemo(() => payloadTonnesSuggestions(vehicleDetails), [vehicleDetails]);
  // Driver "largest vehicle" choices: the roster's classes, including ones only suggested so far.
  const driverWeightClasses = useMemo(
    () => [
      ...payloadOptions,
      ...[...inferredByReg.values()].map((i) => String(i.suggestions?.typical_tonnes?.value ?? '')).filter(Boolean),
    ],
    [payloadOptions, inferredByReg],
  );

  return (
    <div className="w-full min-w-0 space-y-6">
      {/* Payload (T) suggestions for the cell, bulk edit and Add vehicle inputs. */}
      <datalist id={PAYLOAD_OPTIONS_LIST_ID}>
        {payloadOptions.map((v) => (
          <option key={v} value={v} />
        ))}
      </datalist>
      {/* Page header */}
      <div className="pb-2">
        <div className="flex items-center gap-2 mb-2">
          <Truck className="w-8 h-8 text-warning" />
          <h2 className="text-2xl font-bold text-white">Assets</h2>
        </div>
        <p className="text-muted-foreground mt-1">
          Manage fleet vehicles and home depot locations used across Control Centre and reporting.
        </p>
      </div>

      {/* Data sources — membership filters (vendor systems only) */}
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Data sources</span>
          <Button
            variant="secondary" size="sm" type="button"
            onClick={() => setDataSourceFilter('all')}
            className={`border transition-all hover:opacity-90 ${
              dataSourceFilter === 'all'
                ? 'ring-2 ring-offset-1 ring-offset-card ring-muted-foreground text-white bg-accent border-border'
                : 'text-foreground bg-secondary border-border'
            }`}
            title="Show all vehicles"
          >
            All ({vehicleDetails.length})
          </Button>
          {filterableSourcesPresent
            .filter((source) => source !== 'manual')
            .map((source) => {
            const meta = SOURCE_META[source];
            const active = dataSourceFilter === source;
            return (
              <Button
                key={source}
                variant="secondary" size="sm" type="button"
                onClick={() => setDataSourceFilter(source)}
                className={`border transition-all hover:opacity-90 ${
                  active ? meta.chipActive : meta.chipIdle
                }`}
                title={`Show vehicles claimed by ${meta.label} (including matched in other systems)`}
              >
                {meta.label} ({sourceFilterCounts[source]})
              </Button>
            );
          })}
          {hasMultiSourceVehicles && (
            <Button
              variant="secondary" size="sm" type="button"
              onClick={() => setDataSourceFilter('multiple')}
              className={`border transition-all hover:opacity-90 ${
                dataSourceFilter === 'multiple' ? MULTI_SOURCE_CHIP_ACTIVE : MULTI_SOURCE_CHIP_IDLE
              }`}
              title="Show vehicles claimed by 2 or more systems"
            >
              Multiple ({sourceFilterCounts.multiple})
            </Button>
          )}
          {filterableSourcesPresent.includes('manual') && (
            <Button
              variant="secondary" size="sm" type="button"
              onClick={() => setDataSourceFilter('manual')}
              className={`border transition-all hover:opacity-90 ${
                dataSourceFilter === 'manual'
                  ? SOURCE_META.manual.chipActive
                  : SOURCE_META.manual.chipIdle
              }`}
              title="Show vehicles claimed by Manual (including matched in other systems)"
            >
              Manual ({sourceFilterCounts.manual})
            </Button>
          )}
        </div>
      </div>

      {/* Asset Details section */}
      <div className="bg-card/50 rounded-lg p-6 border border-border">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 flex-wrap">
               Asset Details
                <span
                  className="inline-flex items-center justify-center min-w-7 px-2 py-0.5 rounded-md text-sm font-medium bg-secondary text-foreground border border-border"
                  title={
                    dataSourceFilter !== 'all' || vehicleRegFilter.trim()
                      ? `${filteredRows.length} shown of ${vehicleDetails.length} vehicles`
                      : `${vehicleDetails.length} vehicles`
                  }
                >
                  {dataSourceFilter !== 'all' || vehicleRegFilter.trim()
                    ? filteredRows.length
                    : vehicleDetails.length}
                </span>
                <ConfigMetaBadge
                  updatedAt={vehicleConfigMeta.updatedAt}
                  updatedPrefix="Last refreshed"
                  title="When this vehicle roster was last loaded from the Truth Layer"
                />
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="secondary" size="sm" type="button"
                  onClick={() => {
                    if (vehicleDetailsHasChanges) {
                      setConfirmAction('refresh-vehicles');
                      return;
                    }
                    fetchVehicleList({ fresh: true });
                    setVehicleEditMode(false);
                  }}
                  disabled={vehicleListLoading || vehicleListEnriching}
                  className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
                  title="Refresh"
                >
                  <RefreshCw aria-hidden className={`w-4 h-4 group-hover:text-success ${vehicleListLoading || vehicleListEnriching ? 'animate-spin' : ''}`} />
                  <span className="group-hover:text-success transition-all">Refresh</span>
                </Button>
                <Button
                  variant="secondary" size="sm" type="button"
                  onClick={() => {
                    const headers = [
                      'Vehicle Reg',
                      'Data Source',
                      'Vehicle Type',
                      'Payload (T)',
                      'Acquisition',
                      'Deck',
                      'Tail-lift',
                      'ADR',
                      'Moffett',
                      'Small vehicle',
                      'Pallet spaces',
                      'Start Year',
                      'Service Life (yr)',
                      'Home Depot',
                      'Fuel Type',
                      'Unit Cost (£)',
                      'Insurance (£/yr)',
                    ];
                    const csv = [
                      headers.join(','),
                      ...vehicleDetails.map((row, idx) => {
                        const reg = String(row.vehicle_reg ?? '').trim();
                        const isNewRow = newlyAddedRowIndices.includes(idx);
                        const named = sourcesForDataSourceBadge(vehicleSourceSystems.get(reg));
                        const dataSource = dataSourceBadgeLabel(named, isNewRow);
                        return [
                          row.vehicle_reg,
                          dataSource,
                          row.type,
                          row.typical_tonnes ?? '',
                          formatAcquisition(row.acquisition) ? acquisitionLabel(row.acquisition) : '',
                          isTrailerRow(row) && formatTrailerDeck(row.trailer_deck) ? trailerDeckLabel(row.trailer_deck) : '',
                          yesNoLabel(row.tail_lift),
                          yesNoLabel(row.adr_equipped),
                          yesNoLabel(row.moffett),
                          yesNoLabel(row.small_vehicle),
                          formatPalletSpaces(row.pallet_spaces),
                          row.start_year,
                          row.service_life_yr,
                          row.circuit_name,
                          row.fuel,
                          row.unit_cost ?? '',
                          row.insurance_yr ?? '',
                        ].join(',');
                      }),
                    ].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'vehicle_details.csv';
                    link.click();
                    window.URL.revokeObjectURL(url);
                  }}
                  disabled={vehicleDetails.length === 0}
                  className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
                >
                  <Download aria-hidden className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                  <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                </Button>
              </div>
            </div>
            {vehicleListError && vehicleDetails.length === 0 && (
              <p className="text-warning text-sm mb-2" role="alert">
                {vehicleListError}
              </p>
            )}
            {vehicleListSaveError && (
              <p className="text-destructive text-sm mb-2" role="alert">
                {vehicleListSaveError}
              </p>
            )}
            <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
              <div className="min-w-0 flex-1 max-w-xs flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <label htmlFor="vehicle-reg-search" className="block text-xs font-medium text-muted-foreground mb-1">
                    Vehicle Lookup
                  </label>
                  <input
                    id="vehicle-reg-search"
                    type="text"
                    list="vehicle-reg-list"
                    value={vehicleRegFilter}
                    onChange={(e) => setVehicleRegFilter(e.target.value)}
                    placeholder="Type or select to filter..."
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-success focus:border-success"
                    autoComplete="off"
                  />
                  <datalist id="vehicle-reg-list">
                    {vehicleRegOptions.map((reg) => (
                      <option key={reg} value={reg} />
                    ))}
                  </datalist>
                </div>
                {vehicleRegFilter.trim() && (
                  <div className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-success/40 border border-success/60 text-success text-sm mb-0.5">
                    <span className="font-medium truncate max-w-40" title={vehicleRegFilter.trim()}>
                      {vehicleRegFilter.trim()}
                    </span>
                    <Button
                      variant="ghost" size="iconSm" type="button"
                      onClick={() => setVehicleRegFilter('')}
                      className="p-0.5 text-foreground hover:text-white"
                      title="Clear vehicle reg filter"
                      aria-label="Clear vehicle reg filter"
                    >
                      <X aria-hidden className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {vehicleEditMode && lastEditedAt != null && (
                  <span className="text-xs text-muted-foreground self-center" title="Last cell edit in this session">
                    Last edited: {lastEditedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                )}
                {vehicleEditMode && (
                  <Button
                    variant="default" size="sm" type="button"
                    onClick={openAddVehicleForm}
                    disabled={vehicleListSaving}
                    title="Add a new vehicle"
                  >
                    <Plus className="w-3 h-3" aria-hidden />
                    Add vehicle
                  </Button>
                )}
                {vehicleEditMode && inferredByReg.size > 0 && (
                  <Button
                    variant="default" size="sm" type="button"
                    onClick={fillInSuggestions}
                    disabled={vehicleListSaving || !canFillSuggestions}
                    title={
                      selectedRowIndices.size > 0
                        ? 'Copy suggested Tail-lift, ADR, Payload (T) and Pallet spaces values into the empty cells of the selected vehicles. Check them, then Save.'
                        : 'Copy suggested Tail-lift, ADR, Payload (T) and Pallet spaces values into every empty cell. Check them, then Save.'
                    }
                  >
                    Fill in suggestions
                  </Button>
                )}
                {!vehicleEditMode ? (
                  <>
                    <Button
                      variant="warning" size="sm" type="button"
                      onClick={() => void handleVehicleEditAllOrSave()}
                      disabled={vehicleListSaving || confirmSaving || vehicleDetails.length === 0}
                      title="Start editing all vehicle fields"
                      className="text-white"
                    >
                      Edit All
                    </Button>
                    <Button
                      variant="default" size="sm" type="button"
                      onClick={() => void confirmAssetValues()}
                      disabled={
                        vehicleListSaving
                        || confirmSaving
                        || !confirmationsLoadedOk
                        || rosterAwaitingObserved
                        || !hasConfirmableOverrides
                      }
                      loading={confirmSaving}
                      title={
                        !confirmationsLoadedOk
                          ? 'Confirmations have not loaded — refresh Assets'
                          : rosterAwaitingObserved
                            ? 'Source-system values have not loaded yet'
                            : hasConfirmableOverrides
                              ? 'Mark blue Asserted fields as confirmed (green)'
                              : 'No unconfirmed Asserted fields'
                      }
                    >
                      <Check className="w-3 h-3" aria-hidden />
                      Confirm values
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="default" size="sm" type="button"
                      disabled={vehicleListSaving}
                      onClick={revertVehicleEdit}
                      title="Restore the last saved version of this table"
                    >
                      Revert
                    </Button>
                    <Button
                      variant="destructive" size="sm" type="button"
                      disabled={vehicleListSaving}
                      onClick={resetVehicleEdit}
                      title="Reset fields to telematics / blank estimates"
                    >
                      Reset
                    </Button>
                    <Button
                      variant="default" size="sm" type="button"
                      disabled={vehicleListSaving || vehicleDetails.length === 0}
                      onClick={() => void handleVehicleEditAllOrSave()}
                      title="Save the current edits"
                      loading={vehicleListSaving}
                    >
                      <Save className="w-3 h-3" aria-hidden />
                      Save
                    </Button>
                    <Button
                      variant="secondary" size="sm" type="button"
                      disabled={vehicleListSaving}
                      onClick={cancelVehicleEdit}
                      title="Discard unsaved edits and keep the last saved version"
                      className="bg-accent hover:bg-muted-foreground text-foreground"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          {vehicleListLoading && vehicleDetails.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw className="w-8 h-8 animate-spin mr-2" />
              <span>Loading vehicle list…</span>
            </div>
          ) : (
            <>
            {rosterStatusBanner && (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {(vehicleListEnriching || pendingEnrichedRoster) && (
                  <RefreshCw
                    className={`w-3.5 h-3.5 shrink-0 ${vehicleListEnriching ? 'animate-spin' : ''}`}
                  />
                )}
                <span>{rosterStatusBanner}</span>
                {pendingEnrichedRoster && (
                  <Button
                    variant="warning" size="sm" type="button"
                    onClick={applyPendingEnrichedRoster}
                    className="h-auto px-2 py-0.5 text-warning bg-warning/50 hover:bg-warning/60 border border-warning/60"
                  >
                    Apply full roster
                  </Button>
                )}
              </div>
            )}
            {vehicleEditMode && selectedRowIndices.size > 0 && (
              <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-success/50 bg-success/30 px-4 py-3">
                <div className="text-sm text-success font-medium">
                  {selectedRowIndices.size} row{selectedRowIndices.size === 1 ? '' : 's'} selected
                </div>
                <div className="flex flex-wrap items-end gap-2 flex-1 min-w-64">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Field to update</label>
                    <select
                      value={bulkEditField}
                      onChange={(e) => setBulkEditField(e.target.value as BulkEditableVehicleField)}
                      className="bg-background border border-border rounded-md px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-success"
                    >
                      {(Object.keys(BULK_EDIT_FIELD_LABELS) as BulkEditableVehicleField[]).map((field) => (
                        <option key={field} value={field}>
                          {BULK_EDIT_FIELD_LABELS[field]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-40">
                    <label className="block text-xs text-muted-foreground mb-1">New value</label>
                    {bulkEditField === 'acquisition' || bulkEditField === 'trailer_deck' || isYesNoCapabilityField(bulkEditField) ? (
                      <select
                        value={bulkEditValue}
                        onChange={(e) => setBulkEditValue(e.target.value)}
                        className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-success"
                      >
                        <option value="">Not set</option>
                        {(bulkEditField === 'trailer_deck'
                          ? TRAILER_DECK_OPTIONS
                          : isYesNoCapabilityField(bulkEditField)
                            ? YES_NO_OPTIONS
                            : ACQUISITION_OPTIONS
                        ).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                    <input
                      type="text"
                      value={bulkEditValue}
                      onChange={(e) => setBulkEditValue(e.target.value)}
                      placeholder={bulkEditField === 'typical_tonnes' ? 'e.g. 26' : bulkEditField === 'pallet_spaces' ? 'e.g. 14' : 'Enter value…'}
                      list={bulkEditField === 'typical_tonnes' ? PAYLOAD_OPTIONS_LIST_ID : undefined}
                      className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-success"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleBulkApply();
                      }}
                    />
                    )}
                  </div>
                  <Button
                    variant="default" size="md" type="button"
                    onClick={handleBulkApply}
                  >
                    Apply to selected
                  </Button>
                </div>
                <Button
                  variant="ghost" size="sm" type="button"
                  onClick={clearRowSelection}
                  className="text-xs text-muted-foreground hover:text-white"
                >
                  Clear selection
                </Button>
                <p className="w-full text-xs text-muted-foreground">
                  Tip: edit any cell on a selected row to apply that change to all selected rows.
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground mb-3 flex flex-wrap items-center gap-x-4 gap-y-1" role="note">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-foreground" aria-hidden />
                <span className="text-foreground">White = Observed (source system)</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-sky-400" aria-hidden />
                <span className="text-sky-300/90">Blue = Asserted (entered here)</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-success" aria-hidden />
                <span className="text-success/90">Green = Asserted · confirmed</span>
              </span>
              {inferredByReg.size > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm bg-purple-400" aria-hidden />
                  <span className="text-purple-300/90 italic">Purple "Yes?" / "26?" = Inferred suggestion, not saved</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-muted-foreground" aria-hidden />
                <span className="text-muted-foreground">Grey = Unknown</span>
              </span>
            </p>
            <div className="overflow-x-auto overflow-y-auto rounded-lg border border-border max-h-screen-70">
              <table className="w-full min-w-320 text-sm border-separate border-spacing-0">
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-border bg-card">
                    <th className="sticky left-0 z-30 min-w-11 py-3.5 px-3 text-center bg-card">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAllFiltered}
                        className="h-4 w-4 rounded border-border bg-card text-success focus:ring-success focus:ring-offset-0 focus:ring-offset-background cursor-pointer"
                        title={allFilteredSelected ? 'Deselect all visible rows' : 'Select all visible rows'}
                        aria-label={allFilteredSelected ? 'Deselect all visible rows' : 'Select all visible rows'}
                      />
                    </th>
                    <th className="sticky left-11 z-30 min-w-34 bg-card shadow-panel">
                      {renderSortableHeader('vehicle_reg', 'Vehicle Reg', 'left')}
                    </th>
                    <th className="min-w-38">
                      {renderSortableHeader('data_source', 'Data Source', 'left')}
                    </th>
                    <th className="min-w-32">{renderSortableHeader('type', 'Vehicle Type', 'left')}</th>
                    <th className="min-w-28">{renderSortableHeader('typical_tonnes', 'Payload (T)', 'right')}</th>
                    <th className="min-w-32" title="How the vehicle is acquired: purchase, lease or rental">
                      {renderSortableHeader('acquisition', 'Acquisition', 'left')}
                    </th>
                    <th className="min-w-32" title="Trailers only: single or double deck">
                      {renderSortableHeader('trailer_deck', 'Deck', 'left')}
                    </th>
                    {YES_NO_CAPABILITY_FIELDS.map((f) => (
                      <th key={f} className="min-w-26" title={CAPABILITY_FIELD_TITLES[f]}>
                        {renderSortableHeader(f, CAPABILITY_FIELD_LABELS[f], 'left')}
                      </th>
                    ))}
                    <th className="min-w-28" title={CAPABILITY_FIELD_TITLES.pallet_spaces}>
                      {renderSortableHeader('pallet_spaces', CAPABILITY_FIELD_LABELS.pallet_spaces, 'right')}
                    </th>
                    <th className="min-w-26" title="Model year from Samsara fleet vehicles">{renderSortableHeader('start_year', 'Start Year', 'left')}</th>
                    <th className="min-w-32">{renderSortableHeader('service_life_yr', 'Service Life (yr)', 'left')}</th>
                    <th className="min-w-36">{renderSortableHeader('circuit_name', 'Home Depot', 'left')}</th>
                    <th className="min-w-30">{renderSortableHeader('fuel', 'Fuel Type', 'left')}</th>
                    <th className="min-w-32">{renderSortableHeader('unit_cost', 'Unit Cost (£)', 'right')}</th>
                    <th className="min-w-36">{renderSortableHeader('insurance_yr', 'Insurance (£/yr)', 'right')}</th>
                    <th className="min-w-11 bg-card text-center" title="Remove vehicle from list" />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(({ row, originalIndex }) => {
                    const isEditing = (f: EditableVehicleField) => editingCell?.rowIndex === originalIndex && editingCell?.field === f;
                    const isNewRow = newlyAddedRowIndices.includes(originalIndex);
                    const cellMinHeight = 'min-h-11';
                    const baseInputClass = 'w-full max-w-full h-8 box-border px-2.5 py-1 text-sm bg-secondary text-white rounded border-2 border-border focus:outline-none focus:ring-0';
                    const isSelected = selectedRowIndices.has(originalIndex);
                    // Weight-class suggestion: shown only while the cell is empty.
                    const payloadSuggestion = parseTypicalTonnes(String(row.typical_tonnes ?? ''))
                      ? undefined
                      : suggestionFor(inferredByReg.get(String(row.vehicle_reg ?? '').trim()), 'typical_tonnes');
                    const stickyCellBg = isSelected ? 'bg-success/10' : 'bg-background group-hover:bg-card';
                    return (
                      <tr
                        key={originalIndex}
                        ref={originalIndex === newlyAddedRowIndex ? newRowRef : undefined}
                        className={`group border-b border-border/50 hover:bg-secondary/30 transition-all ${isSelected ? 'bg-success/20' : ''}`}
                      >
                        <td className={`sticky left-0 z-raised py-3 px-3 text-center ${cellMinHeight} ${stickyCellBg}`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelection(originalIndex)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-border bg-card text-success focus:ring-success focus:ring-offset-0 focus:ring-offset-background cursor-pointer"
                            aria-label={`Select vehicle ${String(row.vehicle_reg ?? '')}`}
                          />
                        </td>
                        <td
                          className={`sticky left-11 z-raised py-3 px-4 font-medium shadow-panel ${cellMinHeight} ${stickyCellBg} ${isNewRow ? `cursor-pointer ${cellFieldClass(row, originalIndex, 'vehicle_reg')}` : 'text-white'}`}
                          onClick={() => isNewRow && vehicleEditMode && !isEditing('vehicle_reg') && setEditingCell({ rowIndex: originalIndex, field: 'vehicle_reg' })}
                        >
                          {isNewRow && isEditing('vehicle_reg') ? (
                            <input
                              type="text"
                              defaultValue={String(row.vehicle_reg ?? '')}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'vehicle_reg', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'vehicle_reg', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'vehicle_reg', baseInputClass)}
                              autoFocus
                            />
                          ) : (
                            <>
                              <span className="inline-block truncate" title={String(row.vehicle_reg ?? '')}>
                                {String(row.vehicle_reg ?? '')}
                              </span>
                              {deviceNotes.get(String(row.vehicle_reg ?? '')) ? (
                                <span className="block text-xs font-normal text-amber-300/90 leading-tight mt-0.5">
                                  {deviceNotes.get(String(row.vehicle_reg ?? ''))}
                                </span>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td className={`py-2 px-4 ${cellMinHeight}`}>
                          {renderDataSourceBadge(String(row.vehicle_reg ?? ''), isNewRow)}
                        </td>
                        <td
                          className={`py-2 px-4 ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'type')} ${vehicleEditMode ? 'cursor-pointer' : ''}`}
                          title={cellFieldTitle(row, 'type')}
                          onClick={() => vehicleEditMode && !isEditing('type') && setEditingCell({ rowIndex: originalIndex, field: 'type' })}
                        >
                          {isEditing('type') ? (
                            <input
                              type="text"
                              defaultValue={String(row.type ?? '')}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'type', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'type', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'type', baseInputClass)}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={String(row.type ?? '')}>{String(row.type ?? '')}</span>
                          )}
                        </td>
                        <td
                          className={`py-2 px-4 text-right ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${payloadSuggestion?.value ? 'text-purple-300 italic' : cellFieldClass(row, originalIndex, 'typical_tonnes')}`}
                          title={payloadSuggestion ? suggestionTitle('typical_tonnes', payloadSuggestion) : cellFieldTitle(row, 'typical_tonnes')}
                          onClick={() => vehicleEditMode && !isEditing('typical_tonnes') && setEditingCell({ rowIndex: originalIndex, field: 'typical_tonnes' })}
                        >
                          <div className="flex items-center justify-end min-h-7">
                            {isEditing('typical_tonnes') ? (
                              <div
                                className="flex items-center justify-end gap-0.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  defaultValue={parseTypicalTonnes(String(row.typical_tonnes ?? ''))}
                                  onBlur={(e) => {
                                    handleVehicleDetailEdit(originalIndex, 'typical_tonnes', e.target.value);
                                    setEditingCell(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') setEditingCell(null);
                                    if (e.key === 'Enter') {
                                      handleVehicleDetailEdit(originalIndex, 'typical_tonnes', (e.target as HTMLInputElement).value);
                                      setEditingCell(null);
                                    }
                                  }}
                                  className={`${cellInputClass(row, 'typical_tonnes', baseInputClass)} text-right w-20`}
                                  list={PAYLOAD_OPTIONS_LIST_ID}
                                  aria-label={`Payload (T) for ${String(row.vehicle_reg ?? '')}`}
                                  autoFocus
                                />
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1">
                                {payloadCostedAsDefault(row.typical_tonnes) ? (
                                  <span
                                    className="text-xs uppercase tracking-wide text-amber-400"
                                    title="No tonnage class for this payload: costed at the Default rates on Configuration"
                                  >
                                    Default
                                  </span>
                                ) : null}
                                {payloadSuggestion?.value
                                  ? `${payloadSuggestion.value}?`
                                  : formatTypicalTonnes(String(row.typical_tonnes ?? ''))}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'acquisition')}`}
                          title={cellFieldTitle(row, 'acquisition')}
                          onClick={() => vehicleEditMode && !isEditing('acquisition') && setEditingCell({ rowIndex: originalIndex, field: 'acquisition' })}
                        >
                          {isEditing('acquisition') ? (
                            <select
                              defaultValue={formatAcquisition(row.acquisition)}
                              onChange={(e) => handleVehicleDetailEdit(originalIndex, 'acquisition', e.target.value)}
                              onBlur={() => setEditingCell(null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'acquisition', baseInputClass)}
                              aria-label={`Acquisition for ${String(row.vehicle_reg ?? '')}`}
                              autoFocus
                            >
                              <option value="">Not set</option>
                              {ACQUISITION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="inline-block truncate">{acquisitionLabel(row.acquisition)}</span>
                          )}
                        </td>
                        {isTrailerRow(row) ? (
                          <td
                            className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'trailer_deck')}`}
                            title={cellFieldTitle(row, 'trailer_deck')}
                            onClick={() => vehicleEditMode && !isEditing('trailer_deck') && setEditingCell({ rowIndex: originalIndex, field: 'trailer_deck' })}
                          >
                            {isEditing('trailer_deck') ? (
                              <select
                                defaultValue={formatTrailerDeck(row.trailer_deck)}
                                onChange={(e) => handleVehicleDetailEdit(originalIndex, 'trailer_deck', e.target.value)}
                                onBlur={() => setEditingCell(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className={cellInputClass(row, 'trailer_deck', baseInputClass)}
                                aria-label={`Deck for ${String(row.vehicle_reg ?? '')}`}
                                autoFocus
                              >
                                <option value="">Not set</option>
                                {TRAILER_DECK_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="inline-block truncate">{trailerDeckLabel(row.trailer_deck)}</span>
                            )}
                          </td>
                        ) : (
                          <td className={`py-2 px-4 ${cellMinHeight} text-gray-600`} title="Deck applies to trailers only">
                            n/a
                          </td>
                        )}
                        {YES_NO_CAPABILITY_FIELDS.map((f) =>
                          renderYesNoCapabilityCell(row, originalIndex, f, isEditing(f), cellMinHeight, baseInputClass),
                        )}
                        {renderPalletSpacesCell(row, originalIndex, isEditing('pallet_spaces'), cellMinHeight, baseInputClass)}
                        <td
                          className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'start_year')}`}
                          title={cellFieldTitle(row, 'start_year')}
                          onClick={() => vehicleEditMode && !isEditing('start_year') && setEditingCell({ rowIndex: originalIndex, field: 'start_year' })}
                        >
                          {isEditing('start_year') ? (
                            <input
                              type="text"
                              inputMode="numeric"
                              defaultValue={String(row.start_year ?? '').replace(/—/g, '')}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'start_year', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'start_year', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'start_year', baseInputClass)}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={formatStartYear(row.start_year)}>
                              {formatStartYear(row.start_year)}
                            </span>
                          )}
                        </td>
                        <td
                          className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'service_life_yr')}`}
                          title={cellFieldTitle(row, 'service_life_yr')}
                          onClick={() => vehicleEditMode && !isEditing('service_life_yr') && setEditingCell({ rowIndex: originalIndex, field: 'service_life_yr' })}
                        >
                          {isEditing('service_life_yr') ? (
                            <input
                              type="text"
                              inputMode="numeric"
                              defaultValue={String(row.service_life_yr ?? '').replace(/—/g, '')}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'service_life_yr', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'service_life_yr', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'service_life_yr', baseInputClass)}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={formatServiceLifeYr(String(row.service_life_yr ?? ''))}>
                              {formatServiceLifeYr(String(row.service_life_yr ?? ''))}
                            </span>
                          )}
                        </td>
                        <td
                          className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'circuit_name')}`}
                          title={cellFieldTitle(row, 'circuit_name')}
                          onClick={() => vehicleEditMode && !isEditing('circuit_name') && setEditingCell({ rowIndex: originalIndex, field: 'circuit_name' })}
                        >
                          {isEditing('circuit_name') ? (
                            <input
                              type="text"
                              defaultValue={String(row.circuit_name ?? '')}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'circuit_name', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'circuit_name', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'circuit_name', baseInputClass)}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={String(row.circuit_name ?? '')}>{String(row.circuit_name ?? '')}</span>
                          )}
                        </td>
                        <td
                          className={`py-2 px-4 ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'fuel')}`}
                          title={cellFieldTitle(row, 'fuel')}
                          onClick={() => vehicleEditMode && !isEditing('fuel') && setEditingCell({ rowIndex: originalIndex, field: 'fuel' })}
                        >
                          {isEditing('fuel') ? (
                            <input
                              type="text"
                              defaultValue={String(row.fuel ?? '')}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'fuel', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'fuel', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={cellInputClass(row, 'fuel', baseInputClass)}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={String(row.fuel ?? '')}>{String(row.fuel ?? '')}</span>
                          )}
                        </td>
                        <td
                          className={`py-3 px-4 text-right ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'unit_cost')}`}
                          title={cellFieldTitle(row, 'unit_cost')}
                          onClick={() => vehicleEditMode && !isEditing('unit_cost') && setEditingCell({ rowIndex: originalIndex, field: 'unit_cost' })}
                        >
                          {isEditing('unit_cost') ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              defaultValue={parseMoneyGbp(row.unit_cost)}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'unit_cost', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'unit_cost', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={`${cellInputClass(row, 'unit_cost', baseInputClass)} text-right`}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={formatMoneyGbp(row.unit_cost)}>
                              {formatMoneyGbp(row.unit_cost)}
                            </span>
                          )}
                        </td>
                        <td
                          className={`py-3 px-4 text-right ${vehicleEditMode ? 'cursor-pointer' : ''} ${cellMinHeight} ${cellFieldClass(row, originalIndex, 'insurance_yr')}`}
                          title={cellFieldTitle(row, 'insurance_yr')}
                          onClick={() => vehicleEditMode && !isEditing('insurance_yr') && setEditingCell({ rowIndex: originalIndex, field: 'insurance_yr' })}
                        >
                          {isEditing('insurance_yr') ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              defaultValue={parseMoneyGbp(row.insurance_yr)}
                              onBlur={(e) => handleVehicleDetailEdit(originalIndex, 'insurance_yr', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVehicleDetailEdit(originalIndex, 'insurance_yr', (e.target as HTMLInputElement).value);
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={`${cellInputClass(row, 'insurance_yr', baseInputClass)} text-right`}
                              autoFocus
                            />
                          ) : (
                            <span className="inline-block truncate" title={formatMoneyGbp(row.insurance_yr)}>
                              {formatMoneyGbp(row.insurance_yr)}
                            </span>
                          )}
                        </td>
                        <td className={`py-3 px-3 text-center ${cellMinHeight}`}>
                          {vehicleEditMode && (
                          <Button
                            variant="ghost" size="iconSm" type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              requestRemoveVehicle(originalIndex);
                            }}
                            className="text-muted-foreground hover:text-destructive"
                            title="Remove vehicle from list"
                          >
                            <Trash2 aria-hidden className="w-4 h-4" />
                          </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          <div className="mt-3 pt-3 border-t border-border/80 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              {vehicleAttributesMeta.updatedBy ? (
                <ConfigMetaBadge
                  variant="provenance"
                  updatedAt={vehicleAttributesMeta.updatedAt}
                  updatedBy={vehicleAttributesMeta.updatedBy}
                />
              ) : (
                <p className="text-xs text-muted-foreground">No attribute saves recorded yet</p>
              )}
            </div>
            <ConfigChangeHistoryButton kind="vehicle-attributes" />
          </div>
      </div>

      {/* Drivers: qualifications (largest vehicle class, ADR) */}
      <AssetsDrivers rosterWeightClasses={driverWeightClasses} />

      {/* Home Depot — separate container from Asset Details */}
      <div className="bg-card/50 rounded-lg p-6 border border-border">
            <div className="mb-3 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2 flex-wrap">
                    Home Depot
                    <span
                      className="inline-flex items-center justify-center min-w-7 px-2 py-0.5 rounded-md text-sm font-medium bg-secondary text-foreground border border-border"
                      title={
                        depotNameFilter.trim()
                          ? `${filteredDepots.length} shown of ${homeDepots.length} depots`
                          : `${homeDepots.length} depots`
                      }
                    >
                      {depotNameFilter.trim() ? filteredDepots.length : homeDepots.length}
                    </span>
                    <ConfigMetaBadge
                      version={depotConfigMeta.version}
                      updatedAt={depotConfigMeta.updatedAt}
                    />
                  </h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
                  <Button
                    variant="secondary" size="sm" type="button"
                    onClick={() => {
                      if (homeDepotsHasChanges) {
                        setConfirmAction('refresh-depots');
                        return;
                      }
                      fetchHomeDepots();
                      setDepotEditMode(false);
                    }}
                    disabled={homeDepotsLoading || homeDepotsSaving}
                    className="border border-transparent hover:border-success"
                    title="Refresh home depots"
                  >
                    <RefreshCw aria-hidden className={`w-4 h-4 ${homeDepotsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="secondary" size="sm" type="button"
                    onClick={() => {
                      const headers = ['Depot Name', 'Depot Number', 'Postcode', 'Type', 'Show on map', 'Owned'];
                      const csv = [
                        headers.join(','),
                        ...homeDepots.map((row) =>
                          [row.name, row.depotNumber, row.postcode, row.type, row.showOnMap ? 'yes' : 'no', row.owned ? 'yes' : 'no'].join(','),
                        ),
                      ].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'home_depots.csv';
                      link.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    disabled={homeDepots.length === 0}
                    className="group border border-transparent hover:border-success hover:shadow-lg hover:shadow-success/50"
                    title="Export depots to CSV"
                  >
                    <Download aria-hidden className="w-4 h-4 group-hover:text-success transition-all" />
                    <span className="group-hover:text-success transition-all">Export</span>
                  </Button>
                </div>
              </div>
            </div>
            {homeDepotsError && (
              <p className="text-warning text-sm mb-2" role="alert">
                {homeDepotsError}
              </p>
            )}
            {homeDepotsSaveError && (
              <p className="text-destructive text-sm mb-2" role="alert">
                {homeDepotsSaveError}
              </p>
            )}
            <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
              <div className="min-w-0 flex-1 max-w-xs flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <label htmlFor="depot-name-search" className="block text-xs font-medium text-muted-foreground mb-1">
                    Depot Lookup
                  </label>
                  <input
                    id="depot-name-search"
                    type="text"
                    list="depot-name-list"
                    value={depotNameFilter}
                    onChange={(e) => setDepotNameFilter(e.target.value)}
                    placeholder="Type or select to filter..."
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-success focus:border-success"
                    autoComplete="off"
                  />
                  <datalist id="depot-name-list">
                    {depotNameOptions.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>
                {depotNameFilter.trim() && (
                  <div className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-success/40 border border-success/60 text-success text-sm mb-0.5">
                    <span className="font-medium truncate max-w-40" title={depotNameFilter.trim()}>
                      {depotNameFilter.trim()}
                    </span>
                    <Button
                      variant="ghost" size="iconSm" type="button"
                      onClick={() => setDepotNameFilter('')}
                      className="p-0.5 text-foreground hover:text-white"
                      title="Clear depot filter"
                      aria-label="Clear depot filter"
                    >
                      <X aria-hidden className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {depotEditMode && (
                  <Button
                    variant="default" size="sm" type="button"
                    onClick={handleAddDepot}
                    disabled={homeDepotsSaving}
                    title="Add a new depot"
                  >
                    <Plus className="w-3 h-3" aria-hidden />
                    Add depot
                  </Button>
                )}
                {!depotEditMode ? (
                  <Button
                    variant="warning" size="sm" type="button"
                    onClick={() => void handleDepotEditAllOrSave()}
                    disabled={homeDepotsSaving}
                    title="Start editing all depot fields"
                    className="text-white"
                  >
                    Edit All
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="default" size="sm" type="button"
                      disabled={homeDepotsSaving}
                      onClick={revertDepotEdit}
                      title="Restore the last saved depot list"
                    >
                      Revert
                    </Button>
                    <Button
                      variant="default" size="sm" type="button"
                      disabled={homeDepotsSaving}
                      onClick={() => void handleDepotEditAllOrSave()}
                      title="Save depot changes"
                      loading={homeDepotsSaving}
                    >
                      <Save className="w-3 h-3" aria-hidden />
                      Save
                    </Button>
                    <Button
                      variant="secondary" size="sm" type="button"
                      disabled={homeDepotsSaving}
                      onClick={cancelDepotEdit}
                      title="Discard unsaved depot edits"
                      className="bg-accent hover:bg-muted-foreground text-foreground"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
            {homeDepotsLoading && homeDepots.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground border border-border rounded-lg">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                <span>Loading home depots…</span>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="border-b border-border bg-card">
                      <th className="w-22% py-2 px-3 text-left font-medium text-foreground">Depot Name</th>
                      <th className="w-12% py-2 px-3 text-left font-medium text-foreground">Depot Number</th>
                      <th className="w-12% py-2 px-3 text-left font-medium text-foreground">Postcode</th>
                      <th className="w-16% py-2 px-3 text-left font-medium text-foreground">Type</th>
                      <th className="w-13% py-2 px-3 text-center font-medium text-foreground" title="Show this depot on Control Centre Live Vehicles map">
                        Show on map?
                      </th>
                      <th className="w-13% py-2 px-3 text-center font-medium text-foreground" title="This company operates this Palletways depot — collections, deliveries, and vehicles seen here count as ours">
                        Owned?
                      </th>
                      <th className="w-12% py-2 px-2 text-center font-medium text-foreground" title="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {homeDepots.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 px-4 text-center text-muted-foreground">
                          No home depots configured for this tenant.
                          {depotEditMode ? ' Click Add depot to create one.' : ' Click Edit All to add depots.'}
                        </td>
                      </tr>
                    ) : filteredDepots.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 px-4 text-center text-muted-foreground">
                          No depots match this lookup.
                        </td>
                      </tr>
                    ) : (
                      filteredDepots.map(({ row: depot, originalIndex: rowIndex }) => {
                        const isEditing = (field: EditableDepotField) =>
                          editingDepotCell?.rowIndex === rowIndex && editingDepotCell?.field === field;
                        const depotInputClass =
                          'absolute inset-x-2 inset-y-1.5 h-7 box-border px-2 py-0 text-sm bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success';
                        const editedClass = 'text-warning';
                        const cellBase = 'relative h-10 px-3 py-0 align-middle overflow-hidden';
                        return (
                          <tr key={depot.id || `${depot.name}-${rowIndex}`} className="border-b border-border/50 hover:bg-secondary/30 h-10">
                            <td
                              className={`${cellBase} ${depotEditMode ? 'cursor-pointer' : ''} ${isDepotCellEdited(depot, rowIndex, 'name') ? editedClass : 'text-white'}`}
                              onClick={() => depotEditMode && !isEditing('name') && setEditingDepotCell({ rowIndex, field: 'name' })}
                            >
                              {isEditing('name') ? (
                                <input
                                  type="text"
                                  defaultValue={depot.name}
                                  onBlur={(e) => handleDepotEdit(rowIndex, 'name', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleDepotEdit(rowIndex, 'name', (e.target as HTMLInputElement).value);
                                    if (e.key === 'Escape') setEditingDepotCell(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={depotInputClass}
                                  autoFocus
                                />
                              ) : (
                                <span className="block truncate leading-10">{depot.name || '—'}</span>
                              )}
                            </td>
                            <td
                              className={`${cellBase} ${depotEditMode ? 'cursor-pointer' : ''} ${isDepotCellEdited(depot, rowIndex, 'depotNumber') ? editedClass : 'text-foreground'}`}
                              onClick={() => depotEditMode && !isEditing('depotNumber') && setEditingDepotCell({ rowIndex, field: 'depotNumber' })}
                            >
                              {isEditing('depotNumber') ? (
                                <input
                                  type="text"
                                  defaultValue={depot.depotNumber}
                                  onBlur={(e) => handleDepotEdit(rowIndex, 'depotNumber', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleDepotEdit(rowIndex, 'depotNumber', (e.target as HTMLInputElement).value);
                                    if (e.key === 'Escape') setEditingDepotCell(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={depotInputClass}
                                  autoFocus
                                />
                              ) : (
                                <span className="block truncate leading-10">{depot.depotNumber || '—'}</span>
                              )}
                            </td>
                            <td
                              className={`${cellBase} ${depotEditMode ? 'cursor-pointer' : ''} ${isDepotCellEdited(depot, rowIndex, 'postcode') ? editedClass : 'text-foreground'}`}
                              onClick={() => depotEditMode && !isEditing('postcode') && setEditingDepotCell({ rowIndex, field: 'postcode' })}
                            >
                              {isEditing('postcode') ? (
                                <input
                                  type="text"
                                  defaultValue={depot.postcode}
                                  onBlur={(e) => handleDepotEdit(rowIndex, 'postcode', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleDepotEdit(rowIndex, 'postcode', (e.target as HTMLInputElement).value);
                                    if (e.key === 'Escape') setEditingDepotCell(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={depotInputClass}
                                  autoFocus
                                />
                              ) : (
                                <span className="block truncate leading-10">{depot.postcode || '—'}</span>
                              )}
                            </td>
                            <td
                              className={`${cellBase} ${depotEditMode ? 'cursor-pointer' : ''} ${isDepotCellEdited(depot, rowIndex, 'type') ? editedClass : 'text-foreground'}`}
                              onClick={() => depotEditMode && !isEditing('type') && setEditingDepotCell({ rowIndex, field: 'type' })}
                            >
                              {isEditing('type') ? (
                                <select
                                  defaultValue={depot.type || 'Depot'}
                                  onBlur={(e) => handleDepotEdit(rowIndex, 'type', e.target.value)}
                                  onChange={(e) => handleDepotEdit(rowIndex, 'type', e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className={depotInputClass}
                                  autoFocus
                                >
                                  {DEPOT_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="block truncate leading-10">{depot.type || 'Depot'}</span>
                              )}
                            </td>
                            <td
                              className={`relative h-10 px-3 py-0 align-middle text-center ${isDepotCellEdited(depot, rowIndex, 'showOnMap') ? editedClass : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(depot.showOnMap)}
                                disabled={!depotEditMode}
                                onChange={(e) => handleDepotShowOnMapToggle(rowIndex, e.target.checked)}
                                className="h-4 w-4 rounded border-border bg-secondary text-warning focus:ring-warning focus:ring-offset-card disabled:opacity-60 cursor-pointer disabled:cursor-default"
                                title={depot.showOnMap ? 'Shown on Live Vehicles map' : 'Hidden from Live Vehicles map'}
                                aria-label={`Show ${depot.name || 'depot'} on map`}
                              />
                            </td>
                            <td
                              className={`relative h-10 px-3 py-0 align-middle text-center ${isDepotCellEdited(depot, rowIndex, 'owned') ? editedClass : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(depot.owned)}
                                disabled={!depotEditMode}
                                onChange={(e) => handleDepotOwnedToggle(rowIndex, e.target.checked)}
                                className="h-4 w-4 rounded border-border bg-secondary text-warning focus:ring-warning focus:ring-offset-card disabled:opacity-60 cursor-pointer disabled:cursor-default"
                                title={
                                  depot.owned
                                    ? 'We operate this Palletways depot — jobs and vehicles here count as ours'
                                    : 'Not our Palletways depot — partner site or map pin only'
                                }
                                aria-label={`${depot.name || 'Depot'} is owned by this company`}
                              />
                            </td>
                            <td className="relative h-10 px-2 py-0 align-middle text-center">
                              {depotEditMode && (
                                <Button
                                  variant="ghost" size="iconSm" type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingDeleteDepotIndex(rowIndex);
                                    setConfirmAction('delete-depot');
                                  }}
                                  className="text-muted-foreground hover:text-destructive"
                                  title="Remove depot from list"
                                >
                                  <Trash2 aria-hidden className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {/* Confirmation modal for delete / refresh with unsaved changes */}
      {confirmAction && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
          onClick={() => {
            setConfirmAction(null);
            setPendingDeleteVehicleIndex(null);
                  }}
        >
          <div
            className="bg-card rounded-lg shadow-xl border border-border max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-2">
              {confirmAction === 'delete-vehicle'
                ? 'Delete vehicle?'
                : confirmAction === 'delete-depot'
                  ? 'Delete depot?'
                  : 'Discard unsaved changes?'}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {confirmAction === 'delete-vehicle'
                ? `Remove ${String(vehicleDetails[pendingDeleteVehicleIndex ?? -1]?.vehicle_reg ?? 'this vehicle')} from the list? You still need to click Save to persist this deletion.`
                : confirmAction === 'delete-depot'
                  ? `Remove ${homeDepots[pendingDeleteDepotIndex ?? -1]?.name || 'this depot'} from the list? You still need to click Save to persist this deletion.`
                  : confirmAction === 'refresh-vehicles'
                    ? 'Refreshing will discard unsaved vehicle edits. Continue?'
                    : 'Refreshing will discard unsaved depot edits. Continue?'}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary" size="md" type="button"
                onClick={() => {
                  setConfirmAction(null);
                  setPendingDeleteVehicleIndex(null);
                  setPendingDeleteDepotIndex(null);
                              }}
                className="border border-border text-foreground"
              >
                Cancel
              </Button>
              <Button
                variant={confirmAction === 'delete-vehicle' || confirmAction === 'delete-depot' ? 'destructive' : 'warning'}
                size="md"
                type="button"
                onClick={() => void handleConfirmAction()}
                className={
                  confirmAction === 'delete-vehicle' || confirmAction === 'delete-depot'
                    ? 'text-white bg-destructive/80 hover:bg-destructive border border-destructive'
                    : 'text-white bg-warning/80 hover:bg-warning border border-warning'
                }
              >
                {confirmAction === 'delete-vehicle' || confirmAction === 'delete-depot'
                  ? 'Delete'
                  : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Vehicle Modal - same style as Carbon Reporting "+ Fuel" form */}
      {showAddVehicleForm && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
          onClick={() => setShowAddVehicleForm(false)}
        >
          <div
            className="bg-card rounded-lg shadow-xl border border-border max-w-4xl w-full max-h-screen-90 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plus className="w-5 h-5 text-success" />
                <div>
                  <h2 className="text-xl font-bold text-white">Add vehicle</h2>
                  <p className="text-sm text-muted-foreground">Configure the new vehicle and add it to the list</p>
                </div>
              </div>
              <Button
                variant="ghost" size="icon" type="button"
                onClick={() => setShowAddVehicleForm(false)}
                className="text-2xl leading-none"
              >
                ✕
              </Button>
            </div>
            <div className="p-6">
              <form onSubmit={handleAddVehicleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Vehicle Reg <span className="text-destructive">*</span></label>
                    <input
                      type="text"
                      required
                      value={String(addVehicleForm.vehicle_reg ?? '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, vehicle_reg: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. AB12 CDE"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Vehicle Type</label>
                    <input
                      type="text"
                      value={String(addVehicleForm.type ?? '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, type: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. 26T, 44T"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Payload (T)</label>
                    <input
                      type="text"
                      value={String(addVehicleForm.typical_tonnes ?? '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, typical_tonnes: e.target.value })}
                      list={PAYLOAD_OPTIONS_LIST_ID}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. 26"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Service Life (yr)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={String(addVehicleForm.service_life_yr ?? '').replace(/—/g, '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, service_life_yr: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. 8"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Home Depot</label>
                    <input
                      type="text"
                      value={String(addVehicleForm.circuit_name ?? '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, circuit_name: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. Bicester Depot"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Fuel Type</label>
                    <input
                      type="text"
                      value={String(addVehicleForm.fuel ?? '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, fuel: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. Diesel"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Unit Cost (£)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={String(addVehicleForm.unit_cost ?? '').replace(/—/g, '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, unit_cost: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. 85000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Insurance (£/yr)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={String(addVehicleForm.insurance_yr ?? '').replace(/—/g, '')}
                      onChange={(e) => setAddVehicleForm({ ...addVehicleForm, insurance_yr: e.target.value })}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                      placeholder="e.g. 4200"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="secondary" size="md" type="button"
                    onClick={() => setShowAddVehicleForm(false)}
                    className="text-foreground"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default" size="md" type="submit"
                  >
                    Add to list
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}


    </div>
  );
}
